/**
 * SA Product — variant updates via Section Rendering API, media gallery, quantity.
 */
(function () {
  const SELECTORS = {
    productInfo: "[data-sa-product-info]",
    variantInput: "[data-sa-variant-input]",
    variantIdInput: "[data-sa-variant-id-input]",
    selectedVariantJson: "[data-sa-selected-variant]",
    mediaRoot: "[data-sa-product-media]",
    mediaPanel: "[data-sa-media-panel]",
    mediaThumb: "[data-sa-media-thumb]",
    mediaTab: "[data-sa-media-tab]",
    mediaGroup: "[data-sa-media-group]",
    mediaThumbsGroup: "[data-sa-media-thumbs-group]",
    mediaMain: "[data-sa-media-main]",
    mediaOverflow: "[data-sa-media-overflow-stack]",
    mediaThumbsTrack: "[data-sa-media-thumbs]",
    mediaPager: "[data-sa-media-pager]",
    mediaPagerText: "[data-sa-media-pager-text]",
    quantityInput: "[data-sa-quantity-input]",
    quantityMinus: "[data-sa-quantity-minus]",
    quantityPlus: "[data-sa-quantity-plus]",
    buyNow: "[data-sa-buy-now]",
    productAccessories: "sa-product-accessories",
  };

  const REGIOS_AUTO_ADD_EVENT = "regios-dopp:auto-add-to-cart:update";
  const REGIOS_WAIT_MS = 5000;

  async function getCartJson() {
    const rootUrl = window.theme?.routes?.root_url || "/";
    const cartUrl =
      rootUrl === "/"
        ? "/cart.js"
        : `${String(rootUrl).replace(/\/$/, "")}/cart.js`;
    const response = await fetch(cartUrl, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error("Unable to read cart.");
    return response.json();
  }

  function waitForRegiosAfterAdd(
    baselineCount,
    timeoutMs = REGIOS_WAIT_MS,
  ) {
    return new Promise((resolve) => {
      let done = false;
      const started = Date.now();

      const finish = (reason) => {
        if (done) return;
        done = true;
        window.removeEventListener(REGIOS_AUTO_ADD_EVENT, onRegiosUpdate);
        document.removeEventListener(REGIOS_AUTO_ADD_EVENT, onRegiosUpdate);
        resolve(reason);
      };

      const onRegiosUpdate = async () => {
        // Regios dispatches before its gift /cart/add.js request has always
        // finished. Treat the event only as a prompt to verify the cart;
        // redirecting on the event itself can create checkout without gift.
        try {
          const cart = await getCartJson();
          if ((cart.item_count || 0) > baselineCount) {
            finish("event-and-cart");
          }
        } catch (error) {
          // The polling loop below will retry.
        }
      };

      window.addEventListener(REGIOS_AUTO_ADD_EVENT, onRegiosUpdate);
      document.addEventListener(REGIOS_AUTO_ADD_EVENT, onRegiosUpdate);

      (async () => {
        while (!done && Date.now() - started < timeoutMs) {
          try {
            const cart = await getCartJson();
            if ((cart.item_count || 0) > baselineCount) {
              finish("cart");
              return;
            }
          } catch (error) {
            // Keep polling until timeout.
          }
          await new Promise((resolvePoll) => setTimeout(resolvePoll, 150));
        }
        finish("timeout");
      })();
    });
  }

  async function addBuyNowItems(items) {
    const rootUrl = window.theme?.routes?.root_url || "/";
    const addUrl =
      rootUrl === "/"
        ? "/cart/add.js"
        : `${String(rootUrl).replace(/\/$/, "")}/cart/add.js`;
    const response = await fetch(addUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ items }),
      credentials: "same-origin",
    });
    const data = await readCartAddResponse(response);
    const errorMessage = getCartAddErrorMessage(data, response);
    if (errorMessage) throw new Error(errorMessage);
    return data;
  }

  async function removeExistingBuyNowItems(cart, items) {
    const selectedIds = new Set(items.map((item) => String(item.id)));
    const updates = {};

    (cart?.items || []).forEach((item) => {
      if (selectedIds.has(String(item.id))) {
        updates[item.key || String(item.id)] = 0;
      }
    });

    if (!Object.keys(updates).length) return cart;

    const rootUrl = window.theme?.routes?.root_url || "/";
    const updateUrl =
      rootUrl === "/"
        ? "/cart/update.js"
        : `${String(rootUrl).replace(/\/$/, "")}/cart/update.js`;
    const response = await fetch(updateUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ updates }),
      credentials: "same-origin",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(
        data.description ||
          data.message ||
          "Unable to prepare cart for checkout.",
      );
    }
    return data;
  }

  function buildLineItemProperties(formData) {
    const properties = {};

    formData.forEach((value, name) => {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== "") {
        properties[match[1]] = value;
      }
    });

    return Object.keys(properties).length ? properties : undefined;
  }

  function normalizeCartItem(item) {
    const id = parseInt(item?.id, 10);
    if (!id) return null;

    const normalized = {
      id,
      quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
    };

    if (item.properties && Object.keys(item.properties).length) {
      normalized.properties = item.properties;
    }

    return normalized;
  }

  function getMainQuantityInput(productInfo) {
    if (!productInfo) return null;
    return productInfo.querySelector(
      ".sa-product-quantity [data-sa-quantity-input], sa-quantity-input[data-sa-quantity] [data-sa-quantity-input]",
    );
  }

  function getMainQuantity(productInfo, formData) {
    const fromInput = parseInt(getMainQuantityInput(productInfo)?.value, 10);
    if (!Number.isNaN(fromInput) && fromInput > 0) return fromInput;

    const fromForm = parseInt(formData?.get("quantity"), 10);
    if (!Number.isNaN(fromForm) && fromForm > 0) return fromForm;

    return 1;
  }

  function isAccessoryItemSelected(item) {
    if (!item) return false;
    if (item.classList.contains("sa-product-accessory-item--unavailable")) {
      return false;
    }

    if (item.dataset.saLocked === "true") {
      return true;
    }

    const checkbox = item.querySelector("[data-sa-accessory-checkbox]");
    return Boolean(checkbox?.checked && !checkbox.disabled);
  }

  function getSelectedProductVariantId(productInfo, formData) {
    if (productInfo?.dataset.selectedVariantId) {
      return productInfo.dataset.selectedVariantId;
    }

    const form = productInfo?.productForm;
    const hidden = form?.querySelector(
      '[data-sa-variant-id-input], [name="id"]',
    );
    if (hidden?.value) return hidden.value;

    const checked = productInfo?.querySelector(
      "[data-sa-variant-picker] [data-sa-variant-input]:checked",
    );
    if (checked?.value) return checked.value;

    return formData?.get("id") || "";
  }

  function buildMainLineItemFromFormData(productInfo, formData) {
    const item = {
      id: getSelectedProductVariantId(productInfo, formData),
      quantity: getMainQuantity(productInfo, formData),
    };
    const properties = buildLineItemProperties(formData);

    if (properties) item.properties = properties;
    return normalizeCartItem(item);
  }

  function collectAccessoryItems(productInfo) {
    if (!productInfo) return [];

    const selected = [];
    const seen = new Set();

    const pushRaw = (raw) => {
      const normalized = normalizeCartItem(raw);
      if (!normalized) return;
      const key = `${normalized.id}:${normalized.quantity}`;
      if (seen.has(key)) return;
      seen.add(key);
      selected.push(normalized);
    };

    productInfo
      .querySelectorAll("sa-product-accessories, sa-product-cart-present")
      .forEach((root) => {
        (root.getSelectedItems?.() || []).forEach(pushRaw);
      });

    // Fallback: locked cart-present rows (even if custom element API missed them)
    productInfo
      .querySelectorAll("[data-sa-cart-present-item]")
      .forEach((item) => {
        if (!isAccessoryItemSelected(item)) return;
        pushRaw({
          id: item.dataset.variantId,
          quantity:
            parseInt(
              item.querySelector("[data-sa-accessory-quantity]")?.value,
              10,
            ) || 1,
        });
      });

    return selected;
  }

  function collectPrescriptionItems(productInfo) {
    const item = productInfo
      ?.querySelector("sa-product-prescription")
      ?.getSelectedItem?.();
    const normalized = normalizeCartItem(item);
    return normalized ? [normalized] : [];
  }

  function buildCartItemsFromProductInfo(productInfo, formData) {
    const items = [
      buildMainLineItemFromFormData(productInfo, formData),
      ...collectAccessoryItems(productInfo),
      ...collectPrescriptionItems(productInfo),
    ].filter(Boolean);

    return items;
  }

  function notifyReviewUpdate(productInfo) {
    productInfo?.querySelector("sa-product-review")?.refresh?.();
  }

  function getBuyActionButtons(form) {
    if (!form) return [];
    const section = form.closest("[data-sa-product-section]");
    const stickyButtons = section
      ? [
          ...section.querySelectorAll(
            "sa-product-sticky [data-sa-add-to-cart], sa-product-sticky [data-sa-buy-now]",
          ),
        ]
      : [];

    return [
      ...form.querySelectorAll("[data-sa-add-to-cart]"),
      ...form.querySelectorAll("[data-sa-buy-now]"),
      ...stickyButtons,
    ];
  }

  function setBuyActionsBusy(form, busy, { loadingBtn = null } = {}) {
    getBuyActionButtons(form).forEach((btn) => {
      if (busy) {
        btn.setAttribute("aria-disabled", "true");
        // Only the active action shows hover-button loading stripes.
        if (loadingBtn && btn === loadingBtn) {
          btn.setAttribute("aria-busy", "true");
        } else {
          btn.removeAttribute("aria-busy");
        }
      } else {
        btn.removeAttribute("aria-busy");
        btn.removeAttribute("aria-disabled");
      }
    });
  }

  function showFormError(form, message) {
    const el = form?.querySelector("[data-sa-form-error]");
    if (!el) return;

    if (message) {
      el.hidden = false;
      el.textContent = message;
    } else {
      el.hidden = true;
      el.textContent = "";
    }
  }

  function getFetchConfig(type, method) {
    return (
      window.theme?.utils?.fetchConfig?.(type, method) || {
        method: method || (type === "json" ? "GET" : "POST"),
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
          ...(type === "javascript"
            ? { "Content-Type": "application/json" }
            : {}),
        },
      }
    );
  }

  function collectCartSectionIds() {
    const ids = [];
    document
      .querySelectorAll(
        "cart-drawer[data-section-id], cart-items[data-section-id]",
      )
      .forEach((el) => {
        const id = el.getAttribute("data-section-id");
        if (id && !ids.includes(id)) ids.push(id);
      });
    return ids;
  }

  function formatCartErrors(errors) {
    if (!errors) return "";
    if (typeof errors === "string") return errors;
    if (Array.isArray(errors)) return errors.filter(Boolean).join(" ");
    if (typeof errors === "object") {
      return Object.values(errors).flat().filter(Boolean).join(" ");
    }
    return "";
  }

  function getCartAddErrorMessage(parsedState, response) {
    if (response?.status === 429) {
      return (
        parsedState?.description ||
        parsedState?.message ||
        "Too many requests. Please refresh the page and try again."
      );
    }

    if (!parsedState || typeof parsedState !== "object") {
      return response?.ok ? "" : "Unable to add product to cart.";
    }

    const message =
      formatCartErrors(parsedState.errors) ||
      parsedState.description ||
      (parsedState.status ? parsedState.message : "");

    if (message) return String(message);
    if (response && !response.ok) {
      return parsedState.message || "Unable to add product to cart.";
    }
    return "";
  }

  function showCartAddError(message) {
    if (!message) return;
    if (window.__SL_toast__?.error) {
      window.__SL_toast__.error({ message });
      return;
    }
    window.__SL_toast__?.show?.({ message, type: "error" });
  }

  function getCartPermalink(items) {
    // Buy Now is handled by submitBuyNow. Never expose a cart permalink,
    // because it bypasses /cart/add.js and the Regios app embed.
    return items?.length ? "#" : "";
  }

  function setBuyNowLinkState(el, href, disabled) {
    if (!el) return;
    if (disabled || !href) {
      el.removeAttribute("href");
      el.setAttribute("aria-disabled", "true");
      el.setAttribute("tabindex", "-1");
      el.setAttribute("role", "link");
      return;
    }
    el.setAttribute("href", href);
    el.removeAttribute("aria-disabled");
    el.removeAttribute("tabindex");
    el.removeAttribute("role");
  }

  function syncBuyNowLinks(productInfo) {
    const form = productInfo?.productForm;
    const formData = form ? new FormData(form) : null;
    const items = buildCartItemsFromProductInfo(productInfo, formData);
    const href = getCartPermalink(items);
    const soldOut = Boolean(form?.querySelector("[data-sa-add-to-cart]")?.disabled);
    const root = getProductRoot(productInfo);
    const links = new Set([
      ...(form?.querySelectorAll("[data-sa-buy-now]") || []),
      ...(root?.querySelectorAll("sa-product-sticky [data-sa-buy-now]") || []),
    ]);
    links.forEach((el) => setBuyNowLinkState(el, href, soldOut));
  }

  function isCartAddRateLimited(error, response, parsedState) {
    return (
      Number(error?.status) === 429 ||
      Number(response?.status) === 429 ||
      Number(parsedState?.status) === 429
    );
  }

  async function readCartAddResponse(response) {
    try {
      return await response.json();
    } catch {
      return {
        status: response.status,
        message:
          response.status === 429
            ? "Too many requests. Please refresh the page and try again."
            : "Unable to add product to cart.",
      };
    }
  }

  async function addCartItems(
    items,
    activeElement,
    { openDrawer = true, skipRateLimitToast = false } = {},
  ) {
    const cartAddUrl = window.theme?.routes?.cart_add_url;
    const cartUrl = window.theme?.routes?.cart_url || "/cart";
    if (!cartAddUrl) {
      const message = "Cart route missing";
      showCartAddError(message);
      throw new Error(message);
    }

    const getSectionsUrl = () => {
      let cartPath = "/cart";
      try {
        cartPath = new URL(cartUrl, window.location.origin).pathname;
      } catch (error) {
        cartPath = cartUrl.split("?")[0];
      }

      if (window.location.pathname === cartPath) {
        return window.theme?.routes?.root_url || "/";
      }

      return window.location.pathname || "/";
    };

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }),
    );
    const sectionIds = [
      ...new Set(
        [...sectionsToBundle, ...collectCartSectionIds()].filter(Boolean),
      ),
    ];

    const config = getFetchConfig("javascript");
    config.method = "POST";
    config.headers = {
      ...config.headers,
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    };
    config.body = JSON.stringify({
      items,
      sections: sectionIds,
      sections_url: getSectionsUrl(),
    });

    let response;
    try {
      response = await fetch(cartAddUrl, config);
    } catch {
      const message = "Unable to add product to cart.";
      showCartAddError(message);
      throw new Error(message);
    }

    const parsedState = await readCartAddResponse(response);
    const errorMessage = getCartAddErrorMessage(parsedState, response);

    if (errorMessage) {
      const error = new Error(errorMessage);
      error.status = response?.status || parsedState?.status;
      if (!(skipRateLimitToast && isCartAddRateLimited(error, response, parsedState))) {
        showCartAddError(errorMessage);
      }
      throw error;
    }

    const cartJson = {
      sections: parsedState.sections,
      items: parsedState.items,
    };

    window.theme?.pubsub?.publish?.(
      window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      {
        source: "product-form",
        productVariantId: items[0]?.id,
        cart: cartJson,
      },
    );

    document.dispatchEvent(
      new CustomEvent("ajaxProduct:added", {
        detail: { product: parsedState },
      }),
    );

    if (openDrawer) {
      if (
        document.body.classList.contains("template-cart") ||
        window.theme?.settings?.cartType === "page"
      ) {
        window.location.href = cartUrl;
        return cartJson;
      }

      const sectionId = document
        .querySelector("cart-drawer[data-section-id]")
        ?.getAttribute("data-section-id");
      if (sectionId) {
        window.theme?.cart?.syncHeaderCartCount?.(sectionId);
      }

      if (window.theme?.cart?.openDrawer) {
        await window.theme.cart.openDrawer(activeElement, { force: true });
      } else {
        document.querySelector("cart-drawer")?.show?.(activeElement, true, {
          force: true,
        });
      }
    }

    return cartJson;
  }

  const UPDATE_REGIONS = [
    "SaProductTitle",
    "SaProductPrice",
    "SaProductVariantPicker",
    "SaProductQuantity",
    "SaProductBuyButtons",
  ];

  function parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function getLensButton() {
    return document.querySelector(
      "lensadvizor-btn[data-lensadvizor-btn], #la-select-lenses-btn",
    );
  }

  function getProductRoot(productInfo) {
    return (
      productInfo?.sectionEl ||
      productInfo?.closest?.("[data-sa-product-section]") ||
      document.querySelector(".sa-product")
    );
  }

  function usesLensApp(productInfo) {
    return getProductRoot(productInfo)?.dataset.useLensApp === "true";
  }

  function isLensadvizorEnabled(productInfo) {
    const root = getProductRoot(productInfo);
    if (!root) return false;
    if (root.dataset.useLensApp === "true") {
      return root.classList.contains("is-lensadvizor");
    }
    return true;
  }

  function openLensAdvisor(productInfo) {
    if (usesLensApp(productInfo) && !isLensadvizorEnabled(productInfo)) {
      return false;
    }

    const lensButton = getLensButton();
    if (!lensButton) {
      window.LensAdvizor?.initBuy?.();
      return false;
    }

    const variantId = productInfo?.querySelector?.(
      'input[name="id"], [name="id"]',
    )?.value;
    if (variantId) {
      lensButton.setAttribute("data-la-variant-id", variantId);
    }

    if (typeof lensButton.launchLensAdvizorModal === "function") {
      lensButton.launchLensAdvizorModal(new Event("click"));
      return true;
    }

    lensButton.click();
    window.LensAdvizor?.initBuy?.();
    return typeof lensButton.launchLensAdvizorModal === "function";
  }

  function isLensadvizorFlag(value) {
    return value === true || value === "true";
  }

  function getLensadvizorMap(productInfo) {
    const el = productInfo?.querySelector?.("[data-sa-lensadvizor-map]");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function syncLensadvizorUi(productInfo, input, variantId) {
    const root =
      productInfo?.sectionEl ||
      productInfo?.closest?.("[data-sa-product-section]");
    if (!root) return;
    if (root.dataset.useLensApp !== "true") {
      root.classList.remove("is-lensadvizor");
      return;
    }

    const checked = productInfo?.querySelector?.(
      "[data-sa-variant-picker] [data-sa-variant-input]:checked",
    );
    const resolvedId = String(
      variantId ||
        input?.value ||
        checked?.value ||
        productInfo?.querySelector?.(
          '[data-sa-variant-id-input], input[name="id"]',
        )?.value ||
        productInfo?.dataset?.selectedVariantId ||
        "",
    );

    const map = getLensadvizorMap(productInfo);
    let show;

    if (map && resolvedId && Object.prototype.hasOwnProperty.call(map, resolvedId)) {
      show = isLensadvizorFlag(map[resolvedId]);
    } else if (input) {
      show = isLensadvizorFlag(input.dataset.showLensadvizor);
    } else if (checked) {
      show = isLensadvizorFlag(checked.dataset.showLensadvizor);
    } else {
      show = isLensadvizorFlag(root.dataset.showLensadvizor);
    }

    root.classList.toggle("is-lensadvizor", show);
    root.dataset.showLensadvizor = show ? "true" : "false";
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    const safeThousands = thousands ?? ",";
    const safeDecimal = decimal ?? ".";
    const safePrecision = precision ?? 2;

    if (Number.isNaN(number) || number == null) {
      return "0";
    }

    const fixed = (number / 100).toFixed(safePrecision);
    const parts = fixed.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${safeThousands}`,
    );
    const centsAmount = parts[1] ? `${safeDecimal}${parts[1]}` : "";

    return dollarsAmount + centsAmount;
  }

  function sanitizeMoneyFormat(format) {
    if (!format) return "${{amount}}";
    return String(format)
      .replace(/<[^>]*>/g, "")
      .trim();
  }

  function formatMoneyPlain(cents, format) {
    const moneyFormat = sanitizeMoneyFormat(format || "${{amount}}");

    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    const normalizedCents = parseInt(cents, 10) || 0;
    const placeholderMatch = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch?.[1] || "amount";
    let value = "";

    switch (placeholder) {
      case "amount_no_decimals":
        value = formatWithDelimiters(normalizedCents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(normalizedCents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(normalizedCents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(normalizedCents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(normalizedCents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(normalizedCents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(normalizedCents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(normalizedCents, 2);
        break;
    }

    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value).trim();
  }

  function renderBuyMoney(el, cents, { negative = false, format } = {}) {
    if (!el) return;

    let plain = formatMoneyPlain(cents, format);
    if (negative && !plain.startsWith("-")) {
      plain = `-${plain}`;
    }

    el.innerHTML = `<span class="notranslate ht-money">${plain}</span>`;
  }

  function getBuyButtonsRoot(productInfo) {
    if (!productInfo) return null;
    return document.getElementById(
      `SaProductBuyButtons-${productInfo.sectionId}-${productInfo.productId}`,
    );
  }

  function getStickyPriceRoot(productInfo) {
    if (!productInfo) return null;
    return document.getElementById(
      `SaProductStickyPrice-${productInfo.sectionId}-${productInfo.productId}`,
    );
  }

  function queryById(root, id) {
    if (!root || !id) return null;
    if (typeof root.getElementById === "function") {
      return root.getElementById(id);
    }
    return root.querySelector(`[id="${id}"]`);
  }

  function getMainPriceRoot(productInfo) {
    if (!productInfo) return null;
    return document.getElementById(
      `SaProductPrice-${productInfo.sectionId}-${productInfo.productId}`,
    );
  }

  function applyVariantPriceData(productInfo, price, compareAt) {
    const buyRoot = getBuyButtonsRoot(productInfo);
    if (!buyRoot) return false;

    const nextPrice = parseInt(price, 10);
    if (Number.isNaN(nextPrice)) return false;

    buyRoot.dataset.variantPrice = String(nextPrice);
    const nextCompare = parseInt(compareAt, 10);
    buyRoot.dataset.variantCompareAt = String(
      Number.isNaN(nextCompare) ? 0 : nextCompare,
    );
    updateMainPrice(productInfo, nextPrice, nextCompare);
    updateBuySummary(productInfo);
    return true;
  }

  function updateMainPrice(productInfo, price, compareAt) {
    const root = getMainPriceRoot(productInfo)?.querySelector(
      "[data-sa-main-price]",
    );
    if (!root) return;

    const buyRoot = getBuyButtonsRoot(productInfo);
    const moneyFormat = buyRoot?.dataset.moneyFormat;
    const showDiscounts = buyRoot?.dataset.showDiscounts !== "false";
    const nextPrice = parseInt(price, 10) || 0;
    const nextCompare = parseInt(compareAt, 10) || 0;
    const discount = Math.max(0, nextCompare - nextPrice);
    const currentEl = root.querySelector("[data-sa-main-price-current]");
    const compareRow = root.querySelector("[data-sa-main-price-compare]");
    const compareValue = root.querySelector(
      "[data-sa-main-price-compare-value]",
    );
    const saveRow = root.querySelector("[data-sa-main-price-save]");
    const saveTemplate =
      root.dataset.saveLabelTemplate || "Save up to %%amount%%";

    renderBuyMoney(currentEl, nextPrice, { format: moneyFormat });

    if (showDiscounts && discount > 0) {
      if (compareRow) compareRow.hidden = false;
      if (saveRow) saveRow.hidden = false;
      renderBuyMoney(compareValue, nextCompare, { format: moneyFormat });
      if (saveRow) {
        saveRow.textContent = saveTemplate.replace(
          "%%amount%%",
          formatMoneyPlain(discount, moneyFormat),
        );
      }
    } else {
      if (compareRow) compareRow.hidden = true;
      if (saveRow) saveRow.hidden = true;
    }
  }

  function updateStickySummary(
    productInfo,
    { totalPrice, totalCompare, discount, moneyFormat, showDiscounts },
  ) {
    const stickyRoot = getStickyPriceRoot(productInfo);
    if (!stickyRoot) return;

    const totalEl = stickyRoot.querySelector("[data-sa-sticky-total]");
    const compareRow = stickyRoot.querySelector("[data-sa-sticky-compare]");
    const compareValue = stickyRoot.querySelector(
      "[data-sa-sticky-compare-value]",
    );
    const saveRow = stickyRoot.querySelector("[data-sa-sticky-save]");
    const saveTemplate =
      stickyRoot.dataset.saveLabelTemplate || "Save up to %%amount%%";

    renderBuyMoney(totalEl, totalPrice, { format: moneyFormat });

    if (showDiscounts && discount > 0) {
      if (compareRow) compareRow.hidden = false;
      if (saveRow) saveRow.hidden = false;
      renderBuyMoney(compareValue, totalCompare, { format: moneyFormat });
      if (saveRow) {
        saveRow.textContent = saveTemplate.replace(
          "%%amount%%",
          formatMoneyPlain(discount, moneyFormat),
        );
      }
    } else {
      if (compareRow) compareRow.hidden = true;
      if (saveRow) saveRow.hidden = true;
    }
  }

  function updateBuySummary(productInfo) {
    const buyRoot = getBuyButtonsRoot(productInfo);
    if (!buyRoot) return;

    const variantPrice = parseInt(buyRoot.dataset.variantPrice, 10) || 0;
    const variantCompare =
      parseInt(buyRoot.dataset.variantCompareAt, 10) || variantPrice;
    const labelTemplate =
      buyRoot.dataset.totalLabelTemplate || "Total ({count}):";
    const showDiscounts = buyRoot.dataset.showDiscounts !== "false";
    const moneyFormat = buyRoot.dataset.moneyFormat;
    const mainQty = getMainQuantity(productInfo);

    let itemCount = mainQty;
    let totalPrice = variantPrice * mainQty;
    let totalCompare = variantCompare * mainQty;

    productInfo.querySelectorAll("[data-sa-accessory-item]").forEach((item) => {
      if (!isAccessoryItemSelected(item)) return;

      const price = parseInt(item.dataset.accessoryPrice, 10) || 0;
      const compare = parseInt(item.dataset.accessoryCompareAt, 10) || price;
      const accessoryQty =
        parseInt(
          item.querySelector("[data-sa-accessory-quantity]")?.value,
          10,
        ) || 1;

      itemCount += accessoryQty;
      totalPrice += price * accessoryQty;
      totalCompare += compare * accessoryQty;
    });

    const prescription = productInfo?.querySelector("sa-product-prescription");
    const prescriptionItem = prescription?.getSelectedItem?.();
    if (prescriptionItem) {
      const price = parseInt(prescription.dataset.productPrice, 10) || 0;
      const compare =
        parseInt(prescription.dataset.productCompareAt, 10) || price;
      const qty = prescriptionItem.quantity || 1;
      itemCount += qty;
      totalPrice += price * qty;
      totalCompare += compare * qty;
    }

    const discount = Math.max(0, totalCompare - totalPrice);
    const totalLabel = buyRoot.querySelector("[data-sa-buy-total-label]");
    const totalValue = buyRoot.querySelector("[data-sa-buy-total]");
    const discountRow = buyRoot.querySelector("[data-sa-buy-discount-row]");
    const discountValue = buyRoot.querySelector("[data-sa-buy-discount]");

    if (totalLabel) {
      totalLabel.textContent = labelTemplate.replace("{count}", itemCount);
    }

    if (totalValue) {
      renderBuyMoney(totalValue, totalPrice, { format: moneyFormat });
    }

    if (discountRow && discountValue) {
      if (showDiscounts && discount > 0) {
        discountRow.hidden = false;
        renderBuyMoney(discountValue, discount, {
          negative: true,
          format: moneyFormat,
        });
      } else {
        discountRow.hidden = true;
      }
    }

    updateStickySummary(productInfo, {
      totalPrice,
      totalCompare,
      discount,
      moneyFormat,
      showDiscounts,
    });

    syncBuyNowLinks(productInfo);
    notifyReviewUpdate(productInfo);
  }

  function getSectionRoot(parsedHTML, sectionId) {
    return (
      parsedHTML.getElementById(`shopify-section-${sectionId}`) ||
      parsedHTML.querySelector(`#shopify-section-${sectionId}`) ||
      parsedHTML
    );
  }

  function getDeliveryYmdLocal(date) {
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
    };
  }

  function resolveDeliveryToday(date, timezoneRaw) {
    const timezone = String(timezoneRaw || "").trim();
    if (!timezone) return getDeliveryYmdLocal(date);
    return getDeliveryYmdInOffset(date, parseDeliveryOffsetMinutes(timezone));
  }

  function normalizeDeliveryTimezoneOffset(raw) {
    const value = String(raw || "").trim();
    if (!value) return "";
    if (/^[+-]\d{2}:\d{2}$/.test(value)) return value;

    const numeric = parseInt(value, 10);
    if (!Number.isFinite(numeric)) return "";

    const sign = numeric >= 0 ? "+" : "-";
    const abs = Math.abs(numeric);
    return `${sign}${String(abs).padStart(2, "0")}:00`;
  }

  function parseDeliveryOffsetMinutes(offset) {
    const normalized = normalizeDeliveryTimezoneOffset(offset);
    if (!normalized) return -new Date().getTimezoneOffset();
    const sign = normalized.startsWith("-") ? -1 : 1;
    const [hours, minutes] = normalized.slice(1).split(":").map(Number);
    return sign * (hours * 60 + (minutes || 0));
  }

  function getDeliveryYmdInOffset(date, offsetMinutes) {
    const utcMs = date.getTime() + date.getTimezoneOffset() * 60000;
    const zoned = new Date(utcMs + offsetMinutes * 60000);
    return {
      year: zoned.getUTCFullYear(),
      month: zoned.getUTCMonth(),
      day: zoned.getUTCDate(),
    };
  }

  function addDeliveryCalendarDays(ymd, days) {
    const date = new Date(Date.UTC(ymd.year, ymd.month, ymd.day));
    date.setUTCDate(date.getUTCDate() + days);
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
    };
  }

  const EN_ABBREV_MONTH =
    /^(Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)$/i;

  function formatDeliveryDate(ymd, locale) {
    const date = new Date(ymd.year, ymd.month, ymd.day);
    const lang = String(locale || "")
      .toLowerCase()
      .split(/[-_]/)[0];

    if (lang === "ja") {
      return `${ymd.month + 1}月${ymd.day}日`;
    }

    return new Intl.DateTimeFormat(locale || undefined, {
      month: "short",
      day: "numeric",
    })
      .formatToParts(date)
      .map((part) => {
        if (part.type === "month" && EN_ABBREV_MONTH.test(part.value.trim())) {
          return `${part.value}.`;
        }
        return part.value;
      })
      .join("");
  }

  function resolveDeliveryLocale(el) {
    return (
      el.dataset.deliveryLocale ||
      document.documentElement.lang ||
      navigator.language ||
      "en-US"
    );
  }

  function renderDeliveryTemplate(template, start, end) {
    let text = String(template || "");
    text = text.replace(/\{start\}/gi, start).replace(/\{end\}/gi, end);
    if (text.includes("xxxx")) text = text.replace("xxxx", start);
    if (text.includes("xxx")) text = text.replace("xxx", end);
    return text;
  }

  function initDeliveryEstimates(root = document) {
    root
      .querySelectorAll("[data-sa-buy-delivery][data-delivery-auto]")
      .forEach((el) => {
        const output = el.querySelector("[data-sa-delivery-output]");
        if (!output) return;

        const minDays = parseInt(el.dataset.deliveryMinDays, 10);
        const maxDays = parseInt(el.dataset.deliveryMaxDays, 10);
        if (
          !Number.isFinite(minDays) ||
          !Number.isFinite(maxDays) ||
          minDays < 1 ||
          maxDays < minDays
        ) {
          return;
        }

        const today = resolveDeliveryToday(
          new Date(),
          el.dataset.deliveryTimezone,
        );
        const startDate = addDeliveryCalendarDays(today, minDays);
        const endDate = addDeliveryCalendarDays(today, maxDays);
        const locale = resolveDeliveryLocale(el);
        const start = formatDeliveryDate(startDate, locale);
        const end = formatDeliveryDate(endDate, locale);
        const template =
          el.dataset.deliveryTemplate ||
          "Estimated receipt of your order: {start} to {end}";

        output.textContent = renderDeliveryTemplate(template, start, end);
      });
  }

  class SaQuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector(SELECTORS.quantityInput);
      this.minus = this.querySelector(SELECTORS.quantityMinus);
      this.plus = this.querySelector(SELECTORS.quantityPlus);
      if (!this.input) return;

      this.minus?.addEventListener("click", () => this.step(-1));
      this.plus?.addEventListener("click", () => this.step(1));
      this.input.addEventListener("change", () => this.validate());
      this.validate();
    }

    step(direction) {
      const step = parseInt(this.input.step, 10) || 1;
      const min = parseInt(this.input.min, 10) || 1;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      let value = parseInt(this.input.value, 10) || min;
      value += direction * step;
      if (value < min) value = min;
      if (max != null && value > max) value = max;
      this.input.value = value;
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
      this.validate();
    }

    validate() {
      const value = parseInt(this.input.value, 10);
      const min = parseInt(this.input.min, 10) || 1;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      if (Number.isNaN(value) || value < min) this.input.value = min;
      if (max != null && value > max) this.input.value = max;
      this.minus?.toggleAttribute(
        "disabled",
        parseInt(this.input.value, 10) <= min,
      );
      this.plus?.toggleAttribute(
        "disabled",
        max != null && parseInt(this.input.value, 10) >= max,
      );
    }

    updateRules(parsedHTML, sectionId, productId) {
      const source = queryById(
        parsedHTML,
        `SaProductQuantity-${sectionId}-${productId}`,
      );
      const input = source?.querySelector(SELECTORS.quantityInput);
      if (!input || !this.input) return;

      const currentValue = parseInt(this.input.value, 10);

      ["min", "step", "data-quantity-variant-id"].forEach((attr) => {
        const val = input.getAttribute(attr);
        if (val != null) this.input.setAttribute(attr, val);
      });

      const max = input.getAttribute("max");
      if (max != null) {
        this.input.setAttribute("max", max);
      } else {
        this.input.removeAttribute("max");
      }

      if (!Number.isNaN(currentValue)) {
        this.input.value = String(currentValue);
      }

      this.validate();
    }
  }

  class SaProductMedia extends HTMLElement {
    connectedCallback() {
      this.thumbVisible = parseInt(this.dataset.thumbVisible, 10) || 5;
      this._swipers = new Map();
      this.initSwipers();
      this.bindEvents();
      this.syncThumbVisibility();
      this.syncTabState();
      if (this.activeMediaId)
        this.showMedia(this.activeMediaId, { animate: false });
      this.loadModelViewer();
    }

    disconnectedCallback() {
      this._swipers?.forEach((swiper) => swiper.destroy(true, true));
      this._swipers?.clear();
      if (this._onResize) {
        window.removeEventListener("resize", this._onResize);
      }
    }

    getActiveSwiper(tab = this.activeTab) {
      return this._swipers?.get(tab);
    }

    initSwipers() {
      if (typeof Swiper === "undefined") return;

      this.querySelectorAll(SELECTORS.mediaGroup).forEach((group) => {
        const tab = group.dataset.saMediaGroup;
        const swiperEl = group.querySelector("[data-sa-media-swiper]");
        if (!tab || !swiperEl) return;

        const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
        if (slideCount <= 1) return;

        const prevEl = group.querySelector("[data-sa-media-prev]");
        const nextEl = group.querySelector("[data-sa-media-next]");

        const swiperOptions = {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 300,
          grabCursor: true,
          simulateTouch: true,
          observer: true,
          observeParents: true,
          noSwipingSelector:
            "model-viewer, video, iframe, button, input, textarea, select, label, [data-shopify-xr]",
          navigation:
            prevEl && nextEl
              ? {
                  prevEl,
                  nextEl,
                }
              : undefined,
          on: {
            slideChange: () => this.onSwiperSlideChange(tab),
          },
        };

        const swiper = new Swiper(swiperEl, swiperOptions);

        this._swipers.set(tab, swiper);
      });
    }

    onSwiperSlideChange(tab) {
      if (tab !== this.activeTab) return;
      const swiper = this.getActiveSwiper(tab);
      if (!swiper) return;
      this.syncFromSlideIndex(tab, swiper.activeIndex);
    }

    get activeTab() {
      return this.dataset.activeTab || this.dataset.defaultTab;
    }

    set activeTab(tab) {
      this.dataset.activeTab = tab;
    }

    get activeGroup() {
      return this.querySelector(
        `${SELECTORS.mediaGroup}[data-sa-media-group="${this.activeTab}"]`,
      );
    }

    get activeThumbsGroup() {
      return this.querySelector(
        `${SELECTORS.mediaThumbsGroup}[data-sa-media-thumbs-group="${this.activeTab}"]`,
      );
    }

    get thumbsTrack() {
      return this.activeThumbsGroup?.querySelector(SELECTORS.mediaThumbsTrack);
    }

    get panels() {
      const group = this.activeGroup;
      if (!group) {
        return Array.from(this.querySelectorAll(SELECTORS.mediaPanel));
      }
      return Array.from(group.querySelectorAll(SELECTORS.mediaPanel));
    }

    get thumbs() {
      const group = this.activeThumbsGroup;
      if (!group) {
        return Array.from(this.querySelectorAll(SELECTORS.mediaThumb));
      }
      return Array.from(group.querySelectorAll(SELECTORS.mediaThumb));
    }

    get overflowStack() {
      return this.activeThumbsGroup?.querySelector(SELECTORS.mediaOverflow);
    }

    isMobileViewport() {
      return window.matchMedia("(max-width: 999px)").matches;
    }

    syncThumbVisibility() {
      this.querySelectorAll(SELECTORS.mediaThumb).forEach((thumb) => {
        const index = parseInt(thumb.dataset.mediaIndex, 10);
        if (!Number.isNaN(index)) {
          thumb.hidden = index >= this.thumbVisible;
        }
      });
    }

    areThumbsDisabled() {
      return (
        this.isMobileViewport() &&
        (this.activeTab === "videos" || this.activeTab === "models")
      );
    }

    bindEvents() {
      this._onResize = () => {
        this.syncThumbVisibility();
        this.syncActiveThumb();
      };
      window.addEventListener("resize", this._onResize);

      this.addEventListener("click", (event) => {
        const thumb = event.target.closest(SELECTORS.mediaThumb);
        if (thumb && !thumb.hidden && !this.areThumbsDisabled()) {
          event.preventDefault();
          this.showMedia(thumb.dataset.mediaId);
          return;
        }

        const overflow = event.target.closest(SELECTORS.mediaOverflow);
        if (overflow && !overflow.hidden && !this.areThumbsDisabled()) {
          event.preventDefault();
          this.handleOverflowClick();
          return;
        }

        const tab = event.target.closest(SELECTORS.mediaTab);
        if (tab) {
          event.preventDefault();
          this.activateTab(tab.dataset.saMediaTab, true);
        }
      });
    }

    get activeMediaIndex() {
      const fromId = this.mediaOrder.indexOf(this.activeMediaId);
      if (fromId >= 0) return fromId;
      const swiper = this.getActiveSwiper();
      if (swiper) return swiper.activeIndex;
      return -1;
    }

    get isOverflowActive() {
      return this.activeMediaIndex >= this.thumbVisible;
    }

    get mediaOrder() {
      return this.panels.map((p) => p.dataset.mediaId);
    }

    get activeMediaId() {
      return this.dataset.activeMediaId;
    }

    set activeMediaId(id) {
      this.dataset.activeMediaId = id;
    }

    handleOverflowClick() {
      const order = this.mediaOrder;
      if (!order.length) return;

      let activeIndex = this.activeMediaIndex;
      if (activeIndex < 0) activeIndex = 0;

      const firstOverflowId =
        this.overflowStack?.dataset.firstMediaId || order[this.thumbVisible];

      if (activeIndex < this.thumbVisible && firstOverflowId) {
        this.showMedia(firstOverflowId);
        return;
      }

      if (activeIndex >= this.thumbVisible && activeIndex < order.length - 1) {
        this.showMedia(order[activeIndex + 1]);
      }
    }

    syncOverflowStack() {
      const stack = this.overflowStack;
      if (!stack) return;

      const order = this.mediaOrder;
      const total = order.length;
      if (total <= this.thumbVisible) return;

      const activeIndex = this.activeMediaIndex;
      const preview =
        stack.querySelector("[data-sa-overflow-preview] img") ||
        stack.querySelector("[data-sa-overflow-preview]");
      const countEl = stack.querySelector("[data-sa-overflow-count]");

      let previewIndex = this.thumbVisible;
      if (activeIndex >= this.thumbVisible) {
        previewIndex = Math.min(activeIndex + 1, total - 1);
        stack.classList.add("is-active");
      } else {
        stack.classList.remove("is-active");
      }

      const remaining =
        activeIndex < this.thumbVisible
          ? total - this.thumbVisible
          : total - activeIndex - 1;

      const sourceThumb = this.thumbs.find(
        (t) => parseInt(t.dataset.mediaIndex, 10) === previewIndex,
      );
      const sourceImg = sourceThumb?.querySelector("img");
      if (preview && sourceImg?.src) {
        preview.src = sourceImg.src;
        if (sourceImg.srcset) preview.srcset = sourceImg.srcset;
      }

      if (countEl) {
        if (remaining > 0) {
          countEl.textContent = `+${remaining}`;
          countEl.hidden = false;
        } else {
          countEl.hidden = true;
        }
      }

      stack.dataset.targetMediaId = order[previewIndex] || "";
    }

    scrollThumbIntoView(thumb) {
      if (!thumb || !this.thumbsTrack) return;
      const track = this.thumbsTrack;
      const thumbCenter = thumb.offsetLeft + thumb.offsetWidth / 2;
      const scrollLeft = thumbCenter - track.clientWidth / 2;
      track.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });
    }

    showMedia(mediaId, { animate = true } = {}) {
      if (!mediaId) return;

      const panel = this.querySelector(
        `${SELECTORS.mediaPanel}[data-media-id="${mediaId}"]`,
      );
      if (!panel) return;

      const tabName = panel.dataset.tabGroup;
      if (tabName && tabName !== this.activeTab) {
        this.activateTab(tabName, false);
      }

      const index = parseInt(panel.dataset.mediaIndex, 10);
      const tab = tabName || this.activeTab;
      const swiper = this.getActiveSwiper(tab);

      if (swiper && index >= 0 && swiper.activeIndex !== index) {
        swiper.slideTo(index, animate ? undefined : 0);
      }

      this.syncFromSlideIndex(tab, index);
    }

    syncFromSlideIndex(tab, index) {
      const group = this.querySelector(
        `${SELECTORS.mediaGroup}[data-sa-media-group="${tab}"]`,
      );
      const panel = group?.querySelectorAll(SELECTORS.mediaPanel)?.[index];
      if (!panel) return;

      const mediaId = panel.dataset.mediaId;
      const tabName = panel.dataset.tabGroup || tab;
      this.activeMediaId = mediaId;

      if (!this.areThumbsDisabled()) {
        this.thumbs.forEach((t) => {
          const isActive =
            index >= this.thumbVisible ? false : t.dataset.mediaId === mediaId;
          t.classList.toggle("is-active", isActive);
          t.setAttribute("aria-current", isActive ? "true" : "false");
        });

        if (this.overflowStack) {
          this.overflowStack.classList.toggle(
            "is-active",
            index >= this.thumbVisible,
          );
        }
      }

      this.querySelectorAll(SELECTORS.mediaTab).forEach((tabEl) => {
        const isActive = tabEl.dataset.saMediaTab === tabName;
        tabEl.classList.toggle("is-active", isActive);
        tabEl.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      if (!this.areThumbsDisabled()) {
        this.syncOverflowStack();
        this.syncActiveThumb();
      }
      this.syncNavState();
      this.syncPager();
      this.pauseInactiveVideos();
      this.playActiveVideo(panel);
      this.syncTabsLayout();
    }

    syncTabsLayout() {
      const tab = this.activeTab;
      const tabsBelow = tab === "videos" || tab === "models";

      this.classList.toggle("sa-product-media--tabs-below", tabsBelow);
    }

    syncActiveThumb() {
      if (this.isOverflowActive) {
        const stack = this.overflowStack;
        if (stack && !stack.hidden) {
          requestAnimationFrame(() => this.scrollThumbIntoView(stack));
        }
        return;
      }
      const activeThumb = this.thumbs.find((t) =>
        t.classList.contains("is-active"),
      );
      if (activeThumb && !activeThumb.hidden) {
        requestAnimationFrame(() => this.scrollThumbIntoView(activeThumb));
      }
    }

    activateTab(tabName, pickFirst = true) {
      if (!tabName) return;

      this.activeTab = tabName;

      this.querySelectorAll(SELECTORS.mediaTab).forEach((tab) => {
        const isActive = tab.dataset.saMediaTab === tabName;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      this.querySelectorAll(SELECTORS.mediaGroup).forEach((group) => {
        const isActive = group.dataset.saMediaGroup === tabName;
        group.classList.toggle("is-active", isActive);
        group.hidden = !isActive;
      });

      this.querySelectorAll(SELECTORS.mediaThumbsGroup).forEach((group) => {
        const groupTab = group.dataset.saMediaThumbsGroup;
        const isActiveTab = groupTab === tabName;
        const showImagesThumbsOnMobile =
          this.isMobileViewport() &&
          tabName !== "images" &&
          groupTab === "images";

        group.classList.toggle("is-active", isActiveTab);
        group.hidden = !(isActiveTab || showImagesThumbsOnMobile);
      });

      this.syncThumbVisibility();
      this.syncTabsLayout();

      if (pickFirst) {
        const swiper = this.getActiveSwiper(tabName);
        if (swiper) {
          if (swiper.activeIndex === 0) {
            this.syncFromSlideIndex(tabName, 0);
          } else {
            swiper.slideTo(0);
          }
          return;
        }

        const firstPanel = this.activeGroup?.querySelector(
          SELECTORS.mediaPanel,
        );
        if (firstPanel) {
          this.showMedia(firstPanel.dataset.mediaId, { animate: false });
          return;
        }
      }

      const swiper = this.getActiveSwiper(tabName);
      if (swiper) {
        this.syncFromSlideIndex(tabName, swiper.activeIndex);
        return;
      }

      if (!this.areThumbsDisabled()) {
        this.syncOverflowStack();
        this.syncActiveThumb();
      }
      this.syncNavState();
      this.syncPager();
      this.pauseInactiveVideos();
      this.syncTabsLayout();
    }

    syncTabState() {
      const panel = this.querySelector(
        `${SELECTORS.mediaPanel}[data-media-id="${this.activeMediaId}"]`,
      );
      const tab = panel?.dataset.tabGroup || this.dataset.defaultTab;
      if (tab) this.activateTab(tab, false);
    }

    navigate(direction) {
      const swiper = this.getActiveSwiper();
      if (!swiper) return;
      if (direction < 0) swiper.slidePrev();
      else swiper.slideNext();
    }

    syncNavState() {
      const group = this.activeGroup;
      if (!group) return;
      const prev = group.querySelector("[data-sa-media-prev]");
      const next = group.querySelector("[data-sa-media-next]");
      if (!prev || !next) return;
      const disabled = group.querySelectorAll(SELECTORS.mediaPanel).length <= 1;
      prev.disabled = disabled;
      next.disabled = disabled;
    }

    syncPager() {
      const pager = this.querySelector(SELECTORS.mediaPager);
      const text = pager?.querySelector(SELECTORS.mediaPagerText);
      if (!pager || !text) return;

      const total = this.mediaOrder.length;
      if (total <= 1) {
        pager.hidden = true;
        return;
      }

      const current = Math.max(1, this.activeMediaIndex + 1);
      pager.hidden = false;
      text.textContent = `${current}/${total}`;
    }

    pauseInactiveVideos() {
      this.querySelectorAll("video").forEach((video) => {
        const slide = video.closest(".swiper-slide");
        const group = video.closest(SELECTORS.mediaGroup);
        const isInactiveGroup = group?.hidden;
        const isInactiveSlide =
          slide && !slide.classList.contains("swiper-slide-active");

        if (isInactiveGroup || isInactiveSlide) {
          video.pause();
        }
      });
    }

    playActiveVideo(panel) {
      if (!panel) return;
      const videoInsert = panel.querySelector("video-intersection-insert");
      if (videoInsert?.playMedia) {
        videoInsert.playMedia();
      } else {
        const video = panel.querySelector("video");
        if (video) video.play().catch(() => {});
      }
    }

    loadModelViewer() {
      if (!window.Shopify?.loadFeatures) return;
      window.Shopify.loadFeatures([
        { name: "shopify-xr", version: "1.0" },
        { name: "model-viewer-ui", version: "1.0" },
      ]);
    }
  }

  class SaProductInfo extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onSectionClick = this.onSectionClick.bind(this);
      this.onBuySummaryChange = this.onBuySummaryChange.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.addEventListener("change", this.onVariantChange);
      this.addEventListener("change", this.onBuySummaryChange);
      this.addEventListener("input", this.onBuySummaryChange);
      this.addEventListener("sa:variant-change", this.onBuySummaryChange);
      this.sectionEl = this.closest("[data-sa-product-section]");
      this.sectionEl?.addEventListener("click", this.onSectionClick);
      // Capture on section so submit is caught even if form isn't ready
      // when this element first connects (children parse after open tag).
      this.sectionEl?.addEventListener("submit", this.onFormSubmit, true);
      this.onPageShow = () => updateBuySummary(this);
      window.addEventListener("pageshow", this.onPageShow);
      updateBuySummary(this);
      requestAnimationFrame(() => updateBuySummary(this));
      initDeliveryEstimates(this);
      syncLensadvizorUi(this);
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.onVariantChange);
      this.removeEventListener("change", this.onBuySummaryChange);
      this.removeEventListener("input", this.onBuySummaryChange);
      this.removeEventListener("sa:variant-change", this.onBuySummaryChange);
      this.sectionEl?.removeEventListener("click", this.onSectionClick);
      this.sectionEl?.removeEventListener("submit", this.onFormSubmit, true);
      window.removeEventListener("pageshow", this.onPageShow);
    }

    onBuySummaryChange(event) {
      if (
        event.type === "sa:variant-change" ||
        event.target.closest(
          "[data-sa-accessory-checkbox], [data-sa-accessory-quantity], [data-sa-quantity-input]",
        )
      ) {
        updateBuySummary(this);
      }
    }

    onFormSubmit(event) {
      const form = this.productForm;
      if (!form || event.target !== form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isSubmitting) return;

      const submitter =
        event.submitter || form.querySelector("[data-sa-add-to-cart]");
      if (
        submitter?.disabled ||
        submitter?.getAttribute("name") === "checkout"
      ) {
        return;
      }

      this.submitCart(submitter);
    }

    async submitCart(activeElement) {
      const form = this.productForm;
      if (!form || this.isSubmitting) return;

      const engravingError = this.querySelector(
        "sa-product-prescription",
      )?.getAddToCartError?.();
      if (engravingError) {
        showFormError(form, engravingError);
        return;
      }

      const formData = new FormData(form);
      const items = buildCartItemsFromProductInfo(this, formData);
      if (!items.length) {
        showFormError(form, "Unable to add product to cart.");
        return;
      }

      this.isSubmitting = true;
      showFormError(form, false);
      const addBtn = activeElement?.matches?.("[data-sa-add-to-cart]")
        ? activeElement
        : form.querySelector("[data-sa-add-to-cart]");
      setBuyActionsBusy(form, true, { loadingBtn: addBtn });

      try {
        await addCartItems(items, activeElement || document.activeElement);
      } catch (error) {
        console.error(error);
        if (!window.__SL_toast__) {
          showFormError(
            form,
            error?.message || "Unable to add product to cart.",
          );
        }
      } finally {
        this.isSubmitting = false;
        setBuyActionsBusy(form, false);
      }
    }

    async submitBuyNow(activeElement) {
      const form = this.productForm;
      if (!form || this.isSubmitting) return;

      const engravingError = this.querySelector(
        "sa-product-prescription",
      )?.getAddToCartError?.();
      if (engravingError) {
        showFormError(form, engravingError);
        return;
      }

      const formData = new FormData(form);
      const items = buildCartItemsFromProductInfo(this, formData);
      if (!items.length) {
        showFormError(form, "Unable to add product to cart.");
        return;
      }

      this.isSubmitting = true;
      showFormError(form, false);
      setBuyActionsBusy(form, true, { loadingBtn: activeElement });

      try {
        let cartBefore = null;
        try {
          cartBefore = await getCartJson();
        } catch (error) {
          cartBefore = null;
        }

        // Buy Now should represent the current selection exactly once.
        // Remove stale copies left by a previous attempt, without touching
        // unrelated products already in the cart.
        if (cartBefore) {
          cartBefore = await removeExistingBuyNowItems(cartBefore, items);
        }
        const baseline = cartBefore?.item_count || 0;

        const addedQuantity = items.reduce(
          (total, item) => total + (Number(item.quantity) || 1),
          0,
        );

        // Match Halloween exactly: arm listener → plain /cart/add.js →
        // wait for Regios event/cart growth → /checkout.
        const waitPromise = waitForRegiosAfterAdd(
          baseline + addedQuantity,
        );
        await addBuyNowItems(items);
        await waitPromise;

        const rootUrl = window.theme?.routes?.root_url || "/";
        window.location.href =
          rootUrl === "/"
            ? "/checkout"
            : `${String(rootUrl).replace(/\/$/, "")}/checkout`;
      } catch (error) {
        console.error(error);
        showFormError(
          form,
          error?.message || "Unable to proceed to checkout.",
        );
      } finally {
        this.isSubmitting = false;
        setBuyActionsBusy(form, false);
      }
    }

    get sectionId() {
      return this.dataset.sectionId;
    }

    get productId() {
      return this.dataset.productId;
    }

    get productUrl() {
      return this.dataset.productUrl;
    }

    get productForm() {
      const formId = this.getAttribute("form");
      return formId ? document.getElementById(formId) : null;
    }

    onSectionClick(event) {
      const buyNow = event.target.closest(SELECTORS.buyNow);
      if (buyNow && this.sectionEl?.contains(buyNow)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (
          buyNow.getAttribute("aria-disabled") !== "true" &&
          !this.isSubmitting
        ) {
          this.submitBuyNow(buyNow);
        }
        return;
      }

      const lensTrigger = event.target.closest(
        "[data-sa-select-lenses], [data-sa-sticky-lens]",
      );
      if (lensTrigger) {
        event.preventDefault();
        event.stopPropagation();
        if (openLensAdvisor(this)) return;

        const retry = () => {
          if (openLensAdvisor(this)) {
            document.removeEventListener("LensAdvizor:init:complete", retry);
          }
        };
        document.addEventListener("LensAdvizor:init:complete", retry);
        window.setTimeout(retry, 400);
        window.setTimeout(retry, 1200);
        return;
      }

    }

    onVariantChange(event) {
      const input = event.target.closest(SELECTORS.variantInput);
      if (!input || !this.contains(input)) return;

      const mediaId = input.dataset.featuredMediaId;
      if (mediaId) {
        this.showVariantMedia(mediaId);
      }

      applyVariantPriceData(
        this,
        input.dataset.variantPrice,
        input.dataset.variantCompareAt,
      );
      this.previewReviewSelection(input);
      this.updateVariantInputs(input.value);
      syncLensadvizorUi(this, input);
      this.fetchVariant(input.value);
    }

    previewReviewSelection(input) {
      const reviewEl = document.getElementById(
        `SaProductReview-${this.sectionId}-${this.productId}`,
      );
      reviewEl?.previewSelection?.(input);
    }

    showVariantMedia(mediaId) {
      if (!mediaId) return;

      const mediaEl = document.getElementById(
        `SaProductMediaGallery-${this.sectionId}-${this.productId}`,
      );
      mediaEl?.showMedia?.(String(mediaId));
    }

    getActiveVariantMediaId(variant) {
      const fromVariant = variant?.featured_media?.id;
      if (fromVariant) return String(fromVariant);

      const checkedInput = this.querySelector(
        "[data-sa-variant-picker] [data-sa-variant-input]:checked",
      );
      const fromInput = checkedInput?.dataset.featuredMediaId;
      return fromInput ? String(fromInput) : null;
    }

    syncVariantMedia(variant) {
      const mediaId = this.getActiveVariantMediaId(variant);
      if (!mediaId) return;

      // Run after any synchronous DOM updates from updateRegions.
      requestAnimationFrame(() => {
        this.showVariantMedia(mediaId);
      });
    }

    fetchVariant(variantId) {
      if (!variantId) return;

      this.abortController?.abort();
      this.abortController = new AbortController();

      const url = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;

      fetch(url, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, "text/html");
          this.applyUpdate(parsed, variantId);
        })
        .catch((error) => {
          if (error.name !== "AbortError") console.error(error);
        });
    }

    applyUpdate(parsedHTML, variantId) {
      const sectionRoot = getSectionRoot(parsedHTML, this.sectionId);
      this.updateRegions(sectionRoot);
      this.updateBuyButtons(sectionRoot, variantId);
      this.updateStickyBar(sectionRoot);
      this.updateVariantInputs(variantId);
      this.updateUrl(variantId);

      const variant = parseJson(
        sectionRoot.querySelector(SELECTORS.selectedVariantJson),
      );

      this.syncVariantMedia(variant);

      this.updateReviewMain(sectionRoot);
      syncLensadvizorUi(this, null, variantId);

      this.dispatchEvent(
        new CustomEvent("sa:variant-change", {
          bubbles: true,
          detail: {
            variant,
            sectionId: this.sectionId,
            productId: this.productId,
          },
        }),
      );

      document.dispatchEvent(
        new CustomEvent("variant:change", {
          detail: { variant },
        }),
      );
    }

    updateRegions(sectionRoot) {
      const skipFullReplace = [
        "SaProductBuyButtons",
        "SaProductQuantity",
        "SaProductPrice",
      ];

      UPDATE_REGIONS.forEach((prefix) => {
        if (skipFullReplace.includes(prefix)) return;

        const id = `${prefix}-${this.sectionId}-${this.productId}`;
        const source =
          sectionRoot.querySelector(`#${id}`) ||
          sectionRoot.getElementById?.(id);
        const destination = document.getElementById(id);
        if (!source || !destination) return;

        if (destination.tagName === "SA-PRODUCT-MEDIA") {
          destination.replaceWith(source.cloneNode(true));
        } else {
          destination.innerHTML = source.innerHTML;
        }
      });

      const quantityEl = document.querySelector(
        `sa-quantity-input[data-section-id="${this.sectionId}"][data-product-id="${this.productId}"]`,
      );
      quantityEl?.updateRules?.(sectionRoot, this.sectionId, this.productId);
    }

    updateReviewMain(sectionRoot) {
      const id = `SaProductReview-${this.sectionId}-${this.productId}`;
      const sourceReview = sectionRoot.querySelector(`#${id}`);
      const destReview = document.getElementById(id);
      if (!sourceReview || !destReview) return;

      const sourceMain = sourceReview.querySelector("[data-sa-review-main]");
      const destMain = destReview.querySelector("[data-sa-review-main]");
      if (!sourceMain || !destMain) return;

      destMain.innerHTML = sourceMain.innerHTML;
      destReview.rebindMainRefs?.();
      destReview.applyVariant?.();
      destReview.refresh?.();
    }

    updateBuyButtons(sectionRoot, variantId) {
      const buyButtonsId = `SaProductBuyButtons-${this.sectionId}-${this.productId}`;
      const sourceWrap = queryById(sectionRoot, buyButtonsId);
      const sourceForm = sourceWrap?.querySelector("form");
      const destForm = this.productForm;

      if (sourceWrap) {
        const sourceVariantId = sourceWrap.querySelector('[name="id"]')?.value;
        if (!variantId || String(sourceVariantId) === String(variantId)) {
          applyVariantPriceData(
            this,
            sourceWrap.dataset.variantPrice,
            sourceWrap.dataset.variantCompareAt,
          );
        }
      }

      const variant =
        parseJson(
          sectionRoot.querySelector(
            "sa-product-info > [data-sa-selected-variant]",
          ),
        ) ||
        parseJson(sectionRoot.querySelector(SELECTORS.selectedVariantJson));

      if (variant && (!variantId || String(variant.id) === String(variantId))) {
        applyVariantPriceData(
          this,
          variant.price,
          variant.compare_at_price || 0,
        );
      }

      if (!sourceForm || !destForm) return;

      const nextVariantId =
        variantId || sourceForm.querySelector('[name="id"]')?.value;
      if (nextVariantId) this.updateVariantInputs(nextVariantId);

      const sourceAddBtn = sourceForm.querySelector("[data-sa-add-to-cart]");
      const destAddBtn = destForm.querySelector("[data-sa-add-to-cart]");
      const sourceAddText = sourceForm.querySelector(
        "[data-sa-add-to-cart-text]",
      );
      const destAddText = destForm.querySelector("[data-sa-add-to-cart-text]");
      const sourceBuyNow = sourceForm.querySelector("[data-sa-buy-now]");
      const destBuyNow = destForm.querySelector("[data-sa-buy-now]");

      if (sourceAddBtn && destAddBtn) {
        destAddBtn.disabled = sourceAddBtn.disabled;
      }
      if (sourceAddText && destAddText) {
        destAddText.textContent = sourceAddText.textContent;
      }
      if (sourceBuyNow && destBuyNow) {
        setBuyNowLinkState(
          destBuyNow,
          sourceBuyNow.getAttribute("href") || "",
          sourceBuyNow.getAttribute("aria-disabled") === "true" ||
            sourceAddBtn?.disabled,
        );
      }

      if (window.theme?.variantStrings && destForm.toggleSubmitButton) {
        destForm.toggleSubmitButton(
          !variant?.available,
          window.theme.variantStrings.soldOut,
        );
      }

      syncKlaviyoBis(sourceForm, destForm);
    }

    updateStickyBar(sectionRoot) {
      const financingId = `SaProductStickyFinancing-${this.sectionId}-${this.productId}`;
      const sourceFinancing =
        sectionRoot?.getElementById?.(financingId) ||
        sectionRoot?.querySelector?.(`#${financingId}`);
      const destFinancing = document.getElementById(financingId);
      if (sourceFinancing && destFinancing) {
        destFinancing.innerHTML = sourceFinancing.innerHTML;
        destFinancing.hidden = sourceFinancing.hidden;
      }

      const stickyEl = document.querySelector(
        `sa-product-sticky[data-section-id="${this.sectionId}"][data-product-id="${this.productId}"]`,
      );
      if (!stickyEl) return;

      syncStickyDesc(this.sectionId, this.productId, stickyEl);

      const sourceForm = sectionRoot?.querySelector(
        `#SaProductBuyButtons-${this.sectionId}-${this.productId} form`,
      );
      const destForm = this.productForm;
      if (!sourceForm || !destForm) {
        stickyEl.updateVisibility?.();
        return;
      }

      const sourceAddBtn = sourceForm.querySelector("[data-sa-add-to-cart]");
      const stickyAddBtn = stickyEl.querySelector("[data-sa-add-to-cart]");
      const sourceAddText = sourceForm.querySelector(
        "[data-sa-add-to-cart-text]",
      );
      const stickyAddText = stickyEl.querySelector(
        "[data-sa-add-to-cart-text]",
      );
      const sourceBuyNow = sourceForm.querySelector("[data-sa-buy-now]");
      const stickyBuyNow = stickyEl.querySelector("[data-sa-buy-now]");

      if (sourceAddBtn && stickyAddBtn) {
        stickyAddBtn.disabled = sourceAddBtn.disabled;
      }
      if (sourceAddText && stickyAddText) {
        stickyAddText.textContent = sourceAddText.textContent;
      }
      if (sourceBuyNow && stickyBuyNow) {
        setBuyNowLinkState(
          stickyBuyNow,
          sourceBuyNow.getAttribute("href") || "",
          sourceBuyNow.getAttribute("aria-disabled") === "true" ||
            sourceAddBtn?.disabled,
        );
      }

      syncKlaviyoBis(sourceForm, stickyEl);
      stickyEl.updateVisibility?.();
    }

    updateVariantInputs(variantId) {
      if (!variantId) return;

      this.dataset.selectedVariantId = String(variantId);

      const inputs = new Set();
      const form = this.productForm;

      form
        ?.querySelectorAll('[data-sa-variant-id-input], [name="id"]')
        .forEach((input) => inputs.add(input));
      this.querySelectorAll("[data-sa-variant-id-input]").forEach((input) => {
        inputs.add(input);
      });

      inputs.forEach((input) => {
        if (String(input.value) === String(variantId)) return;
        input.value = variantId;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    updateUrl(variantId) {
      if (this.dataset.updateUrl !== "true" || !variantId) return;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variantId);
      window.history.replaceState({ path: url.toString() }, "", url.toString());
    }
  }

  function parseSaExpiresAt(value) {
    if (!value) return NaN;

    const trimmed = String(value).trim();
    if (!trimmed) return NaN;

    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      return trimmed.length >= 13 ? numeric : numeric * 1000;
    }

    let iso = trimmed.includes(" ") ? trimmed.replace(" ", "T") : trimmed;

    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      iso = `${iso}T23:59:59`;
    }

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(iso)) {
      iso = `${iso}:00`;
    }

    const parsed = Date.parse(iso);
    return Number.isNaN(parsed) ? NaN : parsed;
  }

  function getSaExpiresAtMs(element) {
    const unixSeconds = Number(element?.dataset?.expiresAt);
    if (Number.isFinite(unixSeconds) && unixSeconds > 0) {
      return unixSeconds * 1000;
    }

    return parseSaExpiresAt(element?.dataset?.expires);
  }

  class SaProductCountdown extends HTMLElement {
    connectedCallback() {
      this.root = this.closest(".sa-product-countdown");
      this.expiresAt = getSaExpiresAtMs(this);
      this.parts = {
        days: this.querySelector("[data-sa-countdown-days]"),
        hours: this.querySelector("[data-sa-countdown-hours]"),
        mins: this.querySelector("[data-sa-countdown-mins]"),
        secs: this.querySelector("[data-sa-countdown-secs]"),
      };

      if (
        !Number.isFinite(this.expiresAt) ||
        Object.values(this.parts).some((part) => !part)
      ) {
        this.complete();
        return;
      }

      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) window.clearInterval(this.interval);
    }

    tick() {
      const diff = this.expiresAt - Date.now();

      if (diff <= 0) {
        this.complete();
        return;
      }

      const dayMs = 86400000;
      const hourMs = 3600000;
      const minMs = 60000;

      const days = Math.floor(diff / dayMs);
      const hours = Math.floor((diff % dayMs) / hourMs);
      const mins = Math.floor((diff % hourMs) / minMs);
      const secs = Math.floor((diff % minMs) / 1000);

      this.parts.days.textContent = String(days).padStart(2, "0");
      this.parts.hours.textContent = String(hours).padStart(2, "0");
      this.parts.mins.textContent = String(mins).padStart(2, "0");
      this.parts.secs.textContent = String(secs).padStart(2, "0");
    }

    complete() {
      if (this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }

      if (this.root) {
        this.root.hidden = true;
        this.root.classList.add("is-expired");
      }
    }
  }

  class SaProductCouponCopy extends HTMLElement {
    connectedCallback() {
      this.copyText = this.dataset.copyText || "Copy Code";
      this.copiedText = this.dataset.copiedText || "Copied";
      this.code = this.dataset.code || "";
      this.resetTimer = null;

      this.onActivate = () => this.copy();
      this.onKeydown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.copy();
        }
      };

      this.addEventListener("click", this.onActivate);
      this.addEventListener("keydown", this.onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onActivate);
      this.removeEventListener("keydown", this.onKeydown);
      if (this.resetTimer) window.clearTimeout(this.resetTimer);
    }

    async copy() {
      if (!this.code) return;

      try {
        if (window.isSecureContext && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.code);
        } else {
          const input = document.createElement("textarea");
          input.value = this.code;
          input.setAttribute("readonly", "");
          input.style.position = "absolute";
          input.style.left = "-9999px";
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }

        this.textContent = this.copiedText;
        if (this.resetTimer) window.clearTimeout(this.resetTimer);
        this.resetTimer = window.setTimeout(() => {
          this.textContent = this.copyText;
          this.resetTimer = null;
        }, 5000);
      } catch (error) {
        console.error("Failed to copy discount code:", error);
      }
    }
  }

  class SaProductCouponCountdown extends HTMLElement {
    connectedCallback() {
      this.expiresAt = getSaExpiresAtMs(this);
      this.daysEl = this.querySelector("[data-sa-coupon-days]");
      this.timeEl = this.querySelector("[data-sa-coupon-time]");

      if (!Number.isFinite(this.expiresAt) || !this.daysEl || !this.timeEl) {
        this.complete();
        return;
      }

      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) window.clearInterval(this.interval);
    }

    pad(value) {
      return String(value).padStart(2, "0");
    }

    tick() {
      const diff = this.expiresAt - Date.now();

      if (diff <= 0) {
        this.complete();
        return;
      }

      const dayMs = 86400000;
      const hourMs = 3600000;
      const minMs = 60000;
      const days = Math.floor(diff / dayMs);
      const hours = Math.floor((diff % dayMs) / hourMs);
      const mins = Math.floor((diff % hourMs) / minMs);
      const secs = Math.floor((diff % minMs) / 1000);

      this.daysEl.textContent = String(days);
      this.timeEl.textContent = `${this.pad(hours)}:${this.pad(mins)}:${this.pad(secs)}`;
    }

    complete() {
      if (this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }
      this.classList.add("is-expired");
    }
  }

  class SaProductKeyFeatures extends HTMLElement {
    connectedCallback() {
      this.popup = this.querySelector("[data-sa-feature-popup]");
      this.content = this.popup?.querySelector(
        "[data-sa-feature-popup-content]",
      );
      this.panel = this.popup?.querySelector("[data-sa-feature-popup-panel]");
      this.lastTrigger = null;

      this.onTriggerClick = (event) => {
        const trigger = event.currentTarget;
        const row = trigger.closest(".sa-product-key-features__row");
        const template = row?.querySelector("[data-sa-feature-template]");

        if (!template || !this.content || !this.popup) return;

        const popupStyle =
          row?.dataset.popupStyle === "modal" ? "modal" : "drawer";
        this.popup.classList.remove(
          "sa-product-feature-popup--drawer",
          "sa-product-feature-popup--modal",
        );
        this.popup.classList.add(`sa-product-feature-popup--${popupStyle}`);

        this.content.innerHTML = template.innerHTML;
        this.open(trigger);
      };

      this.onCloseClick = () => this.close();
      this.onKeydown = (event) => {
        if (event.key === "Escape") this.close();
      };

      this.querySelectorAll("[data-sa-feature-trigger]").forEach((trigger) => {
        trigger.addEventListener("click", this.onTriggerClick);
      });

      this.popup
        ?.querySelectorAll("[data-sa-feature-popup-close]")
        .forEach((control) => {
          control.addEventListener("click", this.onCloseClick);
        });
    }

    disconnectedCallback() {
      this.querySelectorAll("[data-sa-feature-trigger]").forEach((trigger) => {
        trigger.removeEventListener("click", this.onTriggerClick);
      });

      this.popup
        ?.querySelectorAll("[data-sa-feature-popup-close]")
        .forEach((control) => {
          control.removeEventListener("click", this.onCloseClick);
        });

      document.removeEventListener("keydown", this.onKeydown);
      document.documentElement.classList.remove("sa-feature-popup-open");
    }

    open(trigger) {
      if (!this.popup) return;

      this.lastTrigger = trigger;
      this.popup.hidden = false;
      this.popup.removeAttribute("inert");
      document.documentElement.classList.add("sa-feature-popup-open");
      document.addEventListener("keydown", this.onKeydown);

      requestAnimationFrame(() => {
        this.popup.classList.add("is-open");
        this.panel?.focus();
      });
    }

    close() {
      if (!this.popup || this.popup.hidden) return;

      this.popup.classList.remove("is-open");
      this.popup.setAttribute("inert", "");
      document.documentElement.classList.remove("sa-feature-popup-open");
      document.removeEventListener("keydown", this.onKeydown);

      window.setTimeout(() => {
        if (!this.popup.classList.contains("is-open")) {
          this.popup.hidden = true;
          this.content.innerHTML = "";
          this.lastTrigger?.focus();
          this.lastTrigger = null;
        }
      }, 300);
    }
  }

  const PRESCRIPTION_MOCK_BLOCKED_WORDS = [
    "test",
    "badword",
    "spam",
    "fuck",
    "shit",
    "傻逼",
    "操你",
  ];

  function parsePrescriptionFilterResponse(data, fallbackText, errorInvalid) {
    const valid =
      data.valid === true ||
      data.success === true ||
      data.pass === true ||
      data.allowed === true;

    if (valid) {
      const filtered =
        data.text ||
        data.filteredText ||
        data.filtered_text ||
        data.data?.text ||
        fallbackText;
      return { valid: true, text: String(filtered) };
    }

    return {
      valid: false,
      message:
        data.message ||
        data.error ||
        data.reason ||
        errorInvalid ||
        "Invalid content",
    };
  }

  async function mockPrescriptionFilter(text, errorInvalid) {
    await new Promise((resolve) => {
      window.setTimeout(resolve, 400);
    });

    const normalized = text.trim().replace(/\s+/g, " ");
    const lowered = normalized.toLowerCase();
    const hasBlockedWord = PRESCRIPTION_MOCK_BLOCKED_WORDS.some((word) =>
      lowered.includes(word.toLowerCase()),
    );

    if (!normalized) {
      return {
        valid: false,
        message: errorInvalid || "Please enter valid prescription information.",
      };
    }

    if (hasBlockedWord) {
      return {
        valid: false,
        message: errorInvalid || "Please enter valid prescription information.",
      };
    }

    return { valid: true, text: normalized };
  }

  class SaProductPrescription extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector("[data-sa-prescription-card]");
      this.footer = this.querySelector("[data-sa-prescription-footer]");
      this.headerBtn = this.querySelector("[data-sa-prescription-header]");
      this.input = this.querySelector("[data-sa-prescription-input]");
      this.clearBtn = this.querySelector("[data-sa-prescription-clear]");
      this.saveBtn = this.querySelector("[data-sa-prescription-save]");
      this.editBtn = this.querySelector("[data-sa-prescription-edit]");
      this.propertyInput = this.querySelector(
        "[data-sa-prescription-property]",
      );
      this.savedText = this.querySelector("[data-sa-prescription-saved-text]");
      this.errorEl = this.querySelector("[data-sa-prescription-error]");
      this.editingPanel = this.querySelector(
        '[data-sa-prescription-panel="editing"]',
      );
      this.savedPanel = this.querySelector(
        '[data-sa-prescription-panel="saved"]',
      );
      this.productInfo = this.closest("sa-product-info");

      this.maxLength = parseInt(this.dataset.maxLength, 10) || 20;
      this.errorMaxLength = this.dataset.errorMaxLength || "";
      this.filterApi = this.dataset.filterApi || "";
      this.errorInvalid = this.dataset.errorInvalid || "";
      this.errorNetwork = this.dataset.errorNetwork || "";
      this.savedValue = "";
      this.desktopMq = window.matchMedia(
        "(min-width: 1000px) and (hover: hover)",
      );

      this.onHeaderClick = () => {
        // Mobile / touch: click to expand. Desktop uses hover.
        if (this.desktopMq.matches) return;
        if (this.card?.getAttribute("data-state") === "collapsed") {
          this.openEditing();
        }
      };
      this.onCardMouseEnter = () => {
        if (!this.desktopMq.matches) return;
        if (this.card?.getAttribute("data-state") === "collapsed") {
          this.openEditing("", { focus: false });
        }
      };
      this.onCardMouseLeave = () => {
        if (!this.desktopMq.matches) return;
        if (this.card?.getAttribute("data-state") !== "editing") return;
        if (this.input && document.activeElement === this.input) return;
        if (this.input?.value.trim()) return;
        this.collapse();
      };
      this.onEditClick = () => this.openEditing(this.savedValue);
      this.onInput = () => {
        this.enforceMaxLength();
        this.syncInputUi();
      };
      this.onKeydown = (event) => this.handleInputKeydown(event);
      this.onPaste = () => {
        this.pendingMaxLengthWarning = true;
      };
      this.onInputBlur = (event) => {
        const next = event.relatedTarget;
        if (next && this.card?.contains(next)) return;

        // Wait a tick so focus can move to Clear/Save, and :hover can settle.
        setTimeout(() => {
          if (this.card?.getAttribute("data-state") !== "editing") return;
          if (this.card?.contains(document.activeElement)) return;
          if (this.input?.value.trim()) return;
          // Still hovering the card on desktop — keep expanded.
          if (this.desktopMq.matches && this.card?.matches(":hover")) return;

          if (this.savedValue) {
            this.showSaved(this.savedValue);
            return;
          }
          this.collapse();
        }, 0);
      };
      this.onClearClick = () => {
        if (!this.input) return;
        this.input.value = "";
        this.syncInputUi();
        this.input.focus();
      };
      this.onSaveClick = () => this.save();

      this.headerBtn?.addEventListener("click", this.onHeaderClick);
      this.card?.addEventListener("mouseenter", this.onCardMouseEnter);
      this.card?.addEventListener("mouseleave", this.onCardMouseLeave);
      this.editBtn?.addEventListener("click", this.onEditClick);
      this.input?.addEventListener("input", this.onInput);
      this.input?.addEventListener("keydown", this.onKeydown);
      this.input?.addEventListener("paste", this.onPaste);
      this.input?.addEventListener("blur", this.onInputBlur);
      this.clearBtn?.addEventListener("click", this.onClearClick);
      this.saveBtn?.addEventListener("click", this.onSaveClick);

      this.setState("collapsed");
      this.syncPropertyInput();
    }

    disconnectedCallback() {
      this.headerBtn?.removeEventListener("click", this.onHeaderClick);
      this.card?.removeEventListener("mouseenter", this.onCardMouseEnter);
      this.card?.removeEventListener("mouseleave", this.onCardMouseLeave);
      this.editBtn?.removeEventListener("click", this.onEditClick);
      this.input?.removeEventListener("input", this.onInput);
      this.input?.removeEventListener("keydown", this.onKeydown);
      this.input?.removeEventListener("paste", this.onPaste);
      this.input?.removeEventListener("blur", this.onInputBlur);
      this.clearBtn?.removeEventListener("click", this.onClearClick);
      this.saveBtn?.removeEventListener("click", this.onSaveClick);
    }

    setState(state) {
      this.card?.setAttribute("data-state", state);
      this.setAttribute("data-state", state);
    }

    syncPropertyInput() {
      if (!this.propertyInput) return;

      if (this.savedValue) {
        this.propertyInput.disabled = false;
        this.propertyInput.value = this.savedValue;
      } else {
        this.propertyInput.value = "";
        this.propertyInput.disabled = true;
      }
    }

    notifyChange() {
      updateBuySummary(this.productInfo);
      this.dispatchEvent(
        new CustomEvent("sa:prescription-change", { bubbles: true }),
      );
    }

    getSelectedItem() {
      if (!this.savedValue) return null;
      if (this.dataset.variantAvailable === "false") return null;

      const variantId = this.dataset.variantId;
      if (!variantId) return null;
      return {
        id: variantId,
        quantity: getMainQuantity(this.productInfo) || 1,
      };
    }

    getAddToCartError() {
      if (!this.savedValue) return null;

      if (this.dataset.engravingProductConfigured === "true") {
        if (
          this.dataset.variantAvailable === "false" ||
          !this.dataset.variantId
        ) {
          return "The engraving add-on is currently unavailable.";
        }
      }

      return null;
    }

    getReviewRow() {
      if (!this.getSelectedItem()) return null;
      return {
        title: this.dataset.reviewLabel || "Personalize Them",
        quantity: getMainQuantity(this.productInfo) || 1,
        price: parseInt(this.dataset.productPrice, 10) || 0,
        compareAt: parseInt(this.dataset.productCompareAt, 10) || 0,
      };
    }

    openEditing(value = "", { focus = true } = {}) {
      this.hideError();
      this.setState("editing");
      if (this.editingPanel) this.editingPanel.hidden = false;
      if (this.savedPanel) this.savedPanel.hidden = true;

      if (this.input) {
        this.input.value = value;
        if (focus) this.input.focus();
      }

      this.syncInputUi();
    }

    collapse() {
      this.hideError();
      this.setState("collapsed");
      if (this.editingPanel) this.editingPanel.hidden = true;
      if (this.savedPanel) this.savedPanel.hidden = true;
      if (this.input) this.input.value = "";
      this.syncInputUi();
    }

    showSaved(value) {
      this.savedValue = value;
      this.setState("saved");
      if (this.editingPanel) this.editingPanel.hidden = true;
      if (this.savedPanel) this.savedPanel.hidden = false;
      if (this.savedText) this.savedText.textContent = value;
      this.syncPropertyInput();
      this.notifyChange();
    }

    getMaxLengthErrorMessage() {
      if (this.errorMaxLength) return this.errorMaxLength;
      return `Maximum ${this.maxLength} characters allowed.`;
    }

    showMaxLengthError() {
      this.showError(this.getMaxLengthErrorMessage(), "max-length");
    }

    enforceMaxLength() {
      if (!this.input) return;

      const value = this.input.value;
      if (value.length <= this.maxLength) {
        if (this.errorEl?.dataset.errorType === "max-length") {
          this.hideError();
        }
        this.pendingMaxLengthWarning = false;
        return;
      }

      this.input.value = value.slice(0, this.maxLength);
      this.showMaxLengthError();
      this.pendingMaxLengthWarning = false;
    }

    handleInputKeydown(event) {
      if (!this.input || event.metaKey || event.ctrlKey || event.altKey) return;

      const allowedKeys = new Set([
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ]);
      if (allowedKeys.has(event.key)) return;

      const value = this.input.value;
      const start = this.input.selectionStart ?? value.length;
      const end = this.input.selectionEnd ?? value.length;
      const selectedLength = Math.max(0, end - start);
      const incomingLength = event.key.length === 1 ? 1 : 0;

      if (value.length - selectedLength + incomingLength <= this.maxLength)
        return;

      event.preventDefault();
      this.showMaxLengthError();
    }

    syncInputUi() {
      const value = this.input?.value || "";
      const trimmed = value.trim();
      const hasValue = trimmed.length > 0;

      if (this.pendingMaxLengthWarning && value.length >= this.maxLength) {
        this.showMaxLengthError();
        this.pendingMaxLengthWarning = false;
      }

      this.clearBtn?.toggleAttribute("hidden", !hasValue);
      this.saveBtn?.toggleAttribute("disabled", !hasValue);
      this.saveBtn?.classList.toggle("is-active", hasValue);
    }

    showError(message, type = "") {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      if (type) {
        this.errorEl.dataset.errorType = type;
      } else {
        delete this.errorEl.dataset.errorType;
      }
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = "";
      this.errorEl.hidden = true;
      delete this.errorEl.dataset.errorType;
    }

    async filterText(text) {
      if (!this.filterApi) {
        return mockPrescriptionFilter(text, this.errorInvalid);
      }

      const response = await fetch(this.filterApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        throw new Error("network");
      }

      const data = await response.json();
      return parsePrescriptionFilterResponse(data, text, this.errorInvalid);
    }

    async save() {
      const value = this.input?.value.trim() || "";
      if (!value) return;

      if (value.length > this.maxLength) {
        this.showMaxLengthError();
        return;
      }

      this.hideError();
      this.saveBtn?.classList.add("is-loading");

      try {
        const result = await this.filterText(value);

        if (!result.valid) {
          this.showError(result.message || this.errorInvalid);
          return;
        }

        this.showSaved(result.text);
      } catch {
        this.showError(this.errorNetwork);
      } finally {
        this.saveBtn?.classList.remove("is-loading");
      }
    }
  }

  class SaProductBundleRecommendations extends HTMLElement {
    connectedCallback() {
      this.initialVisible = parseInt(this.dataset.initialVisible, 10) || 2;
      this.items = [...this.querySelectorAll("[data-sa-bundle-item]")];
      this.toggle = this.querySelector("[data-sa-bundle-toggle]");
      this.toggleLabel = this.querySelector("[data-sa-bundle-toggle-label]");
      this.showMoreLabel = this.dataset.showMoreLabel || "Show More";
      this.showLessLabel = this.dataset.showLessLabel || "Show Less";
      this.expanded = false;

      this.onToggleClick = () => {
        this.expanded = !this.expanded;
        this.updateVisibility();
      };

      if (this.items.length > this.initialVisible) {
        this.toggle.hidden = false;
        this.updateVisibility();
      } else if (this.toggle) {
        this.toggle.hidden = true;
      }

      this.toggle?.addEventListener("click", this.onToggleClick);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener("click", this.onToggleClick);
    }

    updateVisibility() {
      this.items.forEach((item, index) => {
        item.hidden = !this.expanded && index >= this.initialVisible;
      });

      if (this.toggleLabel) {
        this.toggleLabel.textContent = this.expanded
          ? this.showLessLabel
          : this.showMoreLabel;
      }

      this.toggle?.classList.toggle("is-expanded", this.expanded);
    }
  }

  function setAccessoryListExpanded(root, expanded) {
    if (!root) return;
    const initialVisible = parseInt(root.dataset.initialVisible, 10) || 2;
    const showMoreLabel = root.dataset.showMoreLabel || "Show More";
    const showLessLabel = root.dataset.showLessLabel || "Show Less";
    const items = [...root.querySelectorAll("[data-sa-accessory-item]")];
    const toggle = root.querySelector("[data-sa-accessory-toggle]");
    const toggleLabel = root.querySelector("[data-sa-accessory-toggle-label]");

    root.dataset.saExpanded = expanded ? "true" : "false";
    if ("expanded" in root) root.expanded = expanded;

    items.forEach((item, index) => {
      const shouldHide = !expanded && index >= initialVisible;
      if (shouldHide) {
        item.setAttribute("hidden", "");
        item.hidden = true;
      } else {
        item.removeAttribute("hidden");
        item.hidden = false;
      }
    });

    if (toggle) {
      const showToggle = items.length > initialVisible;
      if (showToggle) {
        toggle.removeAttribute("hidden");
        toggle.hidden = false;
      } else {
        toggle.setAttribute("hidden", "");
        toggle.hidden = true;
      }
      toggle.classList.toggle("is-expanded", expanded);
      toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    }

    if (toggleLabel) {
      toggleLabel.textContent = expanded ? showLessLabel : showMoreLabel;
    }
  }

  function onAccessoryToggleClick(event) {
    const toggle = event.target.closest("[data-sa-accessory-toggle]");
    if (!toggle) return;

    const root = toggle.closest(
      "sa-product-cart-present, sa-product-accessories",
    );
    if (!root) return;

    event.preventDefault();
    event.stopPropagation();
    const expanded = root.dataset.saExpanded === "true";
    setAccessoryListExpanded(root, !expanded);
  }

  document.addEventListener("click", onAccessoryToggleClick);

  class SaProductAccessories extends HTMLElement {
    connectedCallback() {
      this.initialVisible = parseInt(this.dataset.initialVisible, 10) || 2;
      this.showMoreLabel = this.dataset.showMoreLabel || "Show More";
      this.showLessLabel = this.dataset.showLessLabel || "Show Less";
      this.expanded = this.dataset.saExpanded === "true";
      this.productInfo = this.closest("sa-product-info");
      this.form = this.productInfo?.productForm || null;

      this.onAccessoryChange = (event) => {
        const lockedCheckbox = event.target.closest("[data-sa-locked-checkbox]");
        if (lockedCheckbox) {
          lockedCheckbox.checked = true;
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (
          !event.target.closest(
            "[data-sa-accessory-checkbox], [data-sa-accessory-quantity]",
          )
        ) {
          return;
        }

        updateBuySummary(this.productInfo);
      };

      this.onLockedClick = (event) => {
        const lockedCheckbox = event.target.closest("[data-sa-locked-checkbox]");
        if (!lockedCheckbox || !this.contains(lockedCheckbox)) return;
        event.preventDefault();
        event.stopPropagation();
        if (!lockedCheckbox.disabled) lockedCheckbox.checked = true;
      };

      this.addEventListener("change", this.onAccessoryChange);
      this.addEventListener("input", this.onAccessoryChange);
      this.addEventListener("click", this.onLockedClick);

      const init = () => {
        this.toggle = this.querySelector("[data-sa-accessory-toggle]");
        this.toggleLabel = this.querySelector(
          "[data-sa-accessory-toggle-label]",
        );
        this.items = [...this.querySelectorAll("[data-sa-accessory-item]")];
        setAccessoryListExpanded(this, this.expanded);
        updateBuySummary(this.productInfo);
      };

      if (this.querySelector("[data-sa-accessory-item]")) {
        init();
      } else {
        queueMicrotask(init);
        requestAnimationFrame(init);
      }
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.onAccessoryChange);
      this.removeEventListener("input", this.onAccessoryChange);
      this.removeEventListener("click", this.onLockedClick);
    }

    getSelectedItems() {
      return [...this.querySelectorAll("[data-sa-accessory-item]")]
        .filter(isAccessoryItemSelected)
        .flatMap((item) => {
          const variantId = item.dataset.variantId;
          const quantityInput = item.querySelector(
            "[data-sa-accessory-quantity]",
          );
          const quantity = parseInt(quantityInput?.value, 10) || 1;

          if (!variantId) return [];
          return [{ id: variantId, quantity }];
        });
    }

    getSelectedReviewRows() {
      return [...this.querySelectorAll("[data-sa-accessory-item]")]
        .filter(isAccessoryItemSelected)
        .map((item) => {
          const quantityInput = item.querySelector(
            "[data-sa-accessory-quantity]",
          );
          return {
            title:
              item.dataset.accessoryTitle ||
              item
                .querySelector(".sa-product-accessory-item__title")
                ?.textContent?.trim() ||
              "",
            quantity: parseInt(quantityInput?.value, 10) || 1,
            price: parseInt(item.dataset.accessoryPrice, 10) || 0,
            compareAt: parseInt(item.dataset.accessoryCompareAt, 10) || 0,
            cartPresent: item.hasAttribute("data-sa-cart-present-item"),
          };
        });
    }

    updateVisibility() {
      setAccessoryListExpanded(this, Boolean(this.expanded));
    }
  }

  function createSaServiceTooltip(trigger) {
    const contentEl =
      trigger.parentElement?.querySelector(
        "[data-sa-service-tooltip-content]",
      ) || trigger.querySelector("[data-sa-service-tooltip-content]");
    const content = contentEl?.innerHTML?.trim();
    if (!content) return null;

    const tip = document.createElement("div");
    tip.className = "sa-service-tooltip";
    tip.setAttribute("role", "tooltip");
    tip.innerHTML = `
      <div class="sa-service-tooltip__inner">${content}</div>
      <span class="sa-service-tooltip__arrow" aria-hidden="true"></span>
    `;
    document.body.appendChild(tip);

    let hideTimer = null;
    let visible = false;

    const position = () => {
      const rect = trigger.getBoundingClientRect();
      tip.classList.add("is-measuring");
      tip.classList.add("is-visible");
      const tipRect = tip.getBoundingClientRect();
      tip.classList.remove("is-measuring");

      let left = rect.left + rect.width / 2 - tipRect.width / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - tipRect.width - 8));

      let top = rect.top - tipRect.height - 10;
      const below = top < 8;
      if (below) top = rect.bottom + 10;

      tip.classList.toggle("is-below", below);
      tip.style.left = `${Math.round(left)}px`;
      tip.style.top = `${Math.round(top)}px`;
    };

    const show = () => {
      clearTimeout(hideTimer);
      visible = true;
      tip.classList.add("is-visible");
      position();
      trigger.setAttribute("aria-expanded", "true");
    };

    const hide = (immediate = false) => {
      const run = () => {
        visible = false;
        tip.classList.remove("is-visible");
        trigger.setAttribute("aria-expanded", "false");
      };
      clearTimeout(hideTimer);
      if (immediate) run();
      else hideTimer = setTimeout(run, 120);
    };

    const onScrollOrResize = () => {
      if (visible) position();
    };

    const onTriggerEnter = () => show();
    const onTriggerLeave = () => hide();
    const onTipEnter = () => {
      clearTimeout(hideTimer);
    };
    const onTipLeave = () => hide();
    const onTriggerClick = (event) => {
      // Touch / click fallback
      if (window.matchMedia("(hover: hover)").matches) return;
      event.preventDefault();
      if (visible) hide(true);
      else show();
    };
    const onDocPointer = (event) => {
      if (!visible) return;
      if (trigger.contains(event.target) || tip.contains(event.target)) return;
      hide(true);
    };

    trigger.setAttribute("aria-expanded", "false");
    trigger.addEventListener("mouseenter", onTriggerEnter);
    trigger.addEventListener("mouseleave", onTriggerLeave);
    trigger.addEventListener("focus", onTriggerEnter);
    trigger.addEventListener("blur", onTriggerLeave);
    trigger.addEventListener("click", onTriggerClick);
    tip.addEventListener("mouseenter", onTipEnter);
    tip.addEventListener("mouseleave", onTipLeave);
    document.addEventListener("pointerdown", onDocPointer);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    return {
      destroy() {
        clearTimeout(hideTimer);
        trigger.removeEventListener("mouseenter", onTriggerEnter);
        trigger.removeEventListener("mouseleave", onTriggerLeave);
        trigger.removeEventListener("focus", onTriggerEnter);
        trigger.removeEventListener("blur", onTriggerLeave);
        trigger.removeEventListener("click", onTriggerClick);
        tip.removeEventListener("mouseenter", onTipEnter);
        tip.removeEventListener("mouseleave", onTipLeave);
        document.removeEventListener("pointerdown", onDocPointer);
        window.removeEventListener("scroll", onScrollOrResize, true);
        window.removeEventListener("resize", onScrollOrResize);
        tip.remove();
      },
    };
  }

  class SaProductReview extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest("sa-product-info");
      this.sectionEl = this.closest("[data-sa-product-section]");
      this.variantChangeRoot = this.sectionEl || this.productInfo;
      this.moneyFormat = this.dataset.moneyFormat || "";
      this.productTitle = this.dataset.productTitle || "";
      this.dynamicEl = this.querySelector("[data-sa-review-dynamic]");
      this.popup = this.querySelector("[data-sa-review-popup]");
      this.popupPanel = this.querySelector("[data-sa-review-popup-panel]");
      this.unpackingGrid = this.querySelector(
        "[data-sa-review-unpacking-grid]",
      );

      this.onVariantChange = (event) => {
        if (
          event.detail?.productId &&
          String(event.detail.productId) !==
            String(this.dataset.productId || "")
        ) {
          return;
        }
        this.applyVariant(event.detail?.variant);
        this.refresh();
      };
      this.onRefresh = () => this.refresh();
      this.onOpenPopup = (event) => {
        event.preventDefault();
        this.openPopup();
      };
      this.onClosePopup = () => this.closePopup();
      this.onKeydown = (event) => {
        if (
          event.key === "Escape" &&
          this.popup?.classList.contains("is-open")
        ) {
          this.closePopup();
        }
      };

      this.rebindMainRefs();
      this.variantChangeRoot?.addEventListener(
        "sa:variant-change",
        this.onVariantChange,
      );
      this.productInfo?.addEventListener(
        "sa:prescription-change",
        this.onRefresh,
      );
      this.productInfo?.addEventListener("change", this.onRefresh);
      this.productInfo?.addEventListener("input", this.onRefresh);
      this.popup
        ?.querySelectorAll("[data-sa-review-popup-close]")
        .forEach((control) => {
          control.addEventListener("click", this.onClosePopup);
        });
      document.addEventListener("keydown", this.onKeydown);

      this.syncUnpackingButton();
      this.refresh();
    }

    disconnectedCallback() {
      this.variantChangeRoot?.removeEventListener(
        "sa:variant-change",
        this.onVariantChange,
      );
      this.productInfo?.removeEventListener(
        "sa:prescription-change",
        this.onRefresh,
      );
      this.productInfo?.removeEventListener("change", this.onRefresh);
      this.productInfo?.removeEventListener("input", this.onRefresh);
      this.unpackingBtn?.removeEventListener("click", this.onOpenPopup);
      this.popup
        ?.querySelectorAll("[data-sa-review-popup-close]")
        .forEach((control) => {
          control.removeEventListener("click", this.onClosePopup);
        });
      document.removeEventListener("keydown", this.onKeydown);
      this.closePopup(true);
    }

    hasUnpackingItems() {
      if (this.dataset.showUnpackingList === "false") return false;
      if (this.dataset.hasUnpacking === "true") return true;
      return Boolean(
        this.unpackingGrid?.querySelector(
          "[data-sa-unpacking-item], .sa-product-unpacking-item",
        ),
      );
    }

    syncUnpackingButton() {
      if (!this.unpackingBtn) return;
      this.unpackingBtn.hidden = !this.hasUnpackingItems();
    }

    rebindMainRefs() {
      this.mainTitleEl = this.querySelector("[data-sa-review-main-title]");
      this.mainQtyEl = this.querySelector("[data-sa-review-main-qty]");
      this.mainPriceEl = this.querySelector("[data-sa-review-main-price]");
      this.bindUnpackingButton();
    }

    bindUnpackingButton() {
      this.unpackingBtn?.removeEventListener("click", this.onOpenPopup);
      this.unpackingBtn = this.querySelector("[data-sa-review-unpacking-open]");
      this.unpackingBtn?.addEventListener("click", this.onOpenPopup);
    }

    getSelectedVariantDisplayName() {
      const checked = this.productInfo?.querySelector(
        "[data-sa-variant-picker] [data-sa-variant-input]:checked",
      );
      const nameEl = checked
        ?.closest(".sa-product-variant__card")
        ?.querySelector(".sa-product-variant__name");
      return nameEl?.textContent?.trim() || "";
    }

    resolveMainPrice(variant) {
      const buyRoot = getBuyButtonsRoot(this.productInfo);
      const checked = this.productInfo?.querySelector(
        "[data-sa-variant-picker] [data-sa-variant-input]:checked",
      );
      const candidates = [
        parseInt(buyRoot?.dataset.variantPrice, 10),
        parseInt(checked?.dataset.variantPrice, 10),
        parseInt(variant?.price, 10),
        parseInt(this.mainPriceEl?.dataset.price, 10),
      ];
      return candidates.find((value) => !Number.isNaN(value));
    }

    previewSelection(input) {
      if (!input) return;

      if (this.dataset.hasMultipleVariants === "true") {
        const card = input.closest(".sa-product-variant__card");
        const name = card
          ?.querySelector(".sa-product-variant__name")
          ?.textContent?.trim();
        if (name && this.mainTitleEl) {
          this.mainTitleEl.textContent = name;
        }
      }

      this.applyVariant({ price: input.dataset.variantPrice });
    }

    applyVariant(variant) {
      let title = this.productTitle;
      if (this.dataset.hasMultipleVariants === "true") {
        title =
          this.getSelectedVariantDisplayName() ||
          (variant?.title && variant.title !== "Default Title"
            ? variant.title
            : this.productTitle);
      }

      if (this.mainTitleEl && title) this.mainTitleEl.textContent = title;

      const price = this.resolveMainPrice(variant);
      if (this.mainPriceEl && price != null) {
        this.mainPriceEl.dataset.price = String(price);
        renderBuyMoney(this.mainPriceEl, price, {
          format: this.moneyFormat,
        });
      }
    }

    getMainQuantity() {
      return getMainQuantity(this.productInfo);
    }

    collectAccessoryRows() {
      const roots = this.productInfo
        ? [
            ...this.productInfo.querySelectorAll(
              "sa-product-accessories, sa-product-cart-present",
            ),
          ]
        : [];
      if (!roots.length) return [];

      const rows = [];
      roots.forEach((root) => {
        if (typeof root.getSelectedReviewRows === "function") {
          rows.push(...root.getSelectedReviewRows());
          return;
        }

        [...root.querySelectorAll("[data-sa-accessory-item]")].forEach(
          (item) => {
            if (!isAccessoryItemSelected(item)) return;

            const quantityInput = item.querySelector(
              "[data-sa-accessory-quantity]",
            );
            rows.push({
              title:
                item.dataset.accessoryTitle ||
                item
                  .querySelector(".sa-product-accessory-item__title")
                  ?.textContent?.trim() ||
                "",
              quantity: parseInt(quantityInput?.value, 10) || 1,
              price: parseInt(item.dataset.accessoryPrice, 10) || 0,
              compareAt: parseInt(item.dataset.accessoryCompareAt, 10) || 0,
              cartPresent: item.hasAttribute("data-sa-cart-present-item"),
            });
          },
        );
      });

      return rows;
    }

    collectPrescriptionRow() {
      const prescription = this.productInfo?.querySelector(
        "sa-product-prescription, .sa-product-prescription",
      );
      if (!prescription) return null;

      if (typeof prescription.getReviewRow === "function") {
        return prescription.getReviewRow();
      }

      if (prescription.getAttribute("data-state") !== "saved") return null;

      return {
        title: prescription.dataset.reviewLabel || "Personalize Them",
        quantity: 1,
        price: parseInt(prescription.dataset.productPrice, 10) || 0,
        compareAt: parseInt(prescription.dataset.productCompareAt, 10) || 0,
      };
    }

    formatPriceHtml(cents, { compare = false } = {}) {
      const plain = formatMoneyPlain(cents, this.moneyFormat);
      if (compare) {
        return `<span class="sa-product-review__compare notranslate ht-money">${plain}</span>`;
      }
      return `<span class="notranslate ht-money">${plain}</span>`;
    }

    buildRowHtml(row, { sale = false, discount = false } = {}) {
      const hasCompare = sale && row.compareAt > row.price && row.compareAt > 0;
      const priceClass = hasCompare
        ? "sa-product-review__price sa-product-review__price--sale"
        : "sa-product-review__price";

      let priceHtml = this.formatPriceHtml(row.price);
      if (hasCompare) {
        priceHtml = `${this.formatPriceHtml(row.price)}${this.formatPriceHtml(
          row.compareAt,
          { compare: true },
        )}`;
      }

      const discountText = row.cartPresent
        ? this.dataset.cartPresentDiscountText || ""
        : this.dataset.discountText || "";
      const discountTagHtml =
        discount && discountText
          ? `<span class="sa-product-discount-tag">${this.escapeHtml(
              discountText,
            )}</span>`
          : "";

      return `
        <div class="sa-product-review__section">
          <div class="sa-product-review__row">
            <div class="sa-product-review__name">
              <span class="sa-product-review__title">${this.escapeHtml(
                row.title,
              )}</span>
              <span class="sa-product-review__qty">x ${row.quantity}</span>
              ${discountTagHtml}
            </div>
            <div class="${priceClass}">${priceHtml}</div>
          </div>
        </div>
      `;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    refresh() {
      if (this.mainQtyEl) {
        this.mainQtyEl.textContent = String(this.getMainQuantity());
      }

      this.syncUnpackingButton();

      if (!this.dynamicEl) return;

      const accessories = this.collectAccessoryRows();
      const prescriptionRow = this.collectPrescriptionRow();

      let html = "";
      accessories.forEach((row) => {
        html += this.buildRowHtml(row, { sale: true, discount: true });
      });
      if (prescriptionRow) {
        html += this.buildRowHtml(prescriptionRow);
      }

      this.dynamicEl.innerHTML = html;
      this.syncDiscountHint(accessories);
    }

    syncDiscountHint(accessories) {
      const hint = this.querySelector("[data-sa-review-discount-hint]");
      if (!hint) return;

      hint.hidden = !(this.dataset.discountText && accessories.length);
    }

    openPopup() {
      if (!this.popup || !this.hasUnpackingItems()) return;

      this.popup.hidden = false;
      this.popup.removeAttribute("inert");
      document.documentElement.classList.add("sa-product-review-popup-open");

      requestAnimationFrame(() => {
        this.popup.classList.add("is-open");
        this.popupPanel?.focus();
      });
    }

    closePopup(force = false) {
      if (!this.popup || this.popup.hidden) return;
      if (!force && !this.popup.classList.contains("is-open")) return;

      this.popup.classList.remove("is-open");
      this.popup.setAttribute("inert", "");
      document.documentElement.classList.remove("sa-product-review-popup-open");

      const finalize = () => {
        if (!this.popup?.classList.contains("is-open")) {
          this.popup.hidden = true;
        }
      };

      if (force || !this.isConnected) {
        finalize();
        return;
      }

      window.setTimeout(finalize, 300);
    }
  }

  function syncKlaviyoBis(sourceRoot, destRoot) {
    if (!sourceRoot || !destRoot) return;

    const sourceAdd = sourceRoot.querySelector("[data-sa-add-to-cart]");
    const destAdd = destRoot.querySelector("[data-sa-add-to-cart]");
    const sourceBis = sourceRoot.querySelector("[data-sa-klaviyo-bis]");
    const destBis = destRoot.querySelector("[data-sa-klaviyo-bis]");

    if (sourceAdd && destAdd) {
      destAdd.hidden = sourceAdd.hidden;
      destAdd.disabled = sourceAdd.disabled;
    }
    if (sourceBis && destBis) destBis.hidden = sourceBis.hidden;
  }

  function syncStickyDesc(sectionId, productId, stickyEl) {
    const source = document.getElementById(
      `SaProductDesc-${sectionId}-${productId}`,
    );
    const dest = stickyEl?.querySelector("[data-sa-sticky-desc]");
    if (!dest) return;

    const text = source?.textContent?.trim() || "";
    dest.textContent = text;
    dest.hidden = !text;
  }

  class SaProductSticky extends HTMLElement {
    constructor() {
      super();
      this.buyObserver = null;
      this.footerObserver = null;
      this.buyTarget = null;
      this.footerEl = null;
      this.buyRetry = 0;
      this.footerRetry = 0;
      this.onScrollLike = null;
      this.scrollBindings = [];
      this.scrollIdleTimer = null;
      this.lastScrollY = 0;
      this.buyVisible = false;
      this.footerVisible = false;
      this.scrollHidden = false;
      this.scrollIdleDelay = 350;
    }

    syncDesc() {
      syncStickyDesc(this.dataset.sectionId, this.dataset.productId, this);
    }

    isSoldOut() {
      const bis = this.querySelector("[data-sa-klaviyo-bis]");
      if (bis) return !bis.hidden;
      return Boolean(this.querySelector("[data-sa-add-to-cart]")?.hidden);
    }

    canShowSticky() {
      return !this.buyVisible && !this.footerVisible && !this.isSoldOut();
    }

    updateVisibility() {
      const canShow = this.canShowSticky();
      this.classList.toggle("is-hidden", !canShow);
      this.classList.toggle("is-scrolling", canShow && this.scrollHidden);
    }

    getScrollY() {
      return (
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0
      );
    }

    resolveBuyTarget() {
      const root = this.closest("[data-sa-product-section]");
      if (!root) return null;

      return (
        root.querySelector(".sa-product-buy__actions") ||
        root.querySelector("[data-sa-product-buy]")
      );
    }

    resolveFooterEl() {
      return (
        document.querySelector("[data-sa-footer]") ||
        document.querySelector('footer[id^="sa-footer-"]')
      );
    }

    syncFooterVisible() {
      const footer = this.footerEl;
      if (!footer) {
        this.footerVisible = false;
        return;
      }

      const rect = footer.getBoundingClientRect();
      this.footerVisible = rect.top < window.innerHeight && rect.bottom > 0;
    }

    markScrolling() {
      this.syncFooterVisible();

      if (!this.canShowSticky()) {
        clearTimeout(this.scrollIdleTimer);
        this.scrollHidden = false;
        this.updateVisibility();
        return;
      }

      if (!this.scrollHidden) {
        this.scrollHidden = true;
        this.updateVisibility();
      }

      clearTimeout(this.scrollIdleTimer);
      this.scrollIdleTimer = window.setTimeout(() => {
        this.scrollHidden = false;
        this.updateVisibility();
      }, this.scrollIdleDelay);
    }

    handleScrollLikeEvent() {
      const nextScrollY = this.getScrollY();
      if (Math.abs(nextScrollY - this.lastScrollY) < 1) return;
      this.lastScrollY = nextScrollY;
      this.markScrolling();
    }

    bindScrollLikeEvents(handler) {
      const captureOptions = { passive: true, capture: true };
      const passiveOptions = { passive: true };

      for (const target of [window, document]) {
        for (const type of ["scroll", "wheel", "touchmove"]) {
          target.addEventListener(type, handler, captureOptions);
          this.scrollBindings.push({
            target,
            type,
            handler,
            options: captureOptions,
          });
        }
      }

      if (window.visualViewport) {
        window.visualViewport.addEventListener(
          "scroll",
          handler,
          passiveOptions,
        );
        this.scrollBindings.push({
          target: window.visualViewport,
          type: "scroll",
          handler,
          options: passiveOptions,
        });
      }
    }

    unbindScrollLikeEvents() {
      this.scrollBindings.forEach(({ target, type, handler, options }) => {
        target.removeEventListener(type, handler, options);
      });
      this.scrollBindings = [];
    }

    initFooterWatch() {
      this.footerEl = this.resolveFooterEl();
      if (!this.footerEl) {
        this.footerRetry += 1;
        if (this.footerRetry < 30) {
          requestAnimationFrame(() => this.initFooterWatch());
        }
        return;
      }

      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          this.footerVisible = entry.isIntersecting;
          this.updateVisibility();
        },
        { threshold: 0 },
      );
      this.footerObserver.observe(this.footerEl);
      this.syncFooterVisible();
      this.updateVisibility();
    }

    initScrollWatch() {
      this.lastScrollY = this.getScrollY();
      this.onScrollLike = () => this.handleScrollLikeEvent();
      this.bindScrollLikeEvents(this.onScrollLike);
      window.addEventListener("resize", this.onScrollLike, { passive: true });
    }

    initBuyWatch() {
      this.buyTarget = this.resolveBuyTarget();
      if (!this.buyTarget) {
        this.buyRetry += 1;
        if (this.buyRetry < 30) {
          requestAnimationFrame(() => this.initBuyWatch());
        }
        return;
      }

      this.syncDesc();

      this.buyObserver = new IntersectionObserver(
        ([entry]) => {
          this.buyVisible = entry.isIntersecting;
          this.updateVisibility();
        },
        { threshold: 0 },
      );
      this.buyObserver.observe(this.buyTarget);

      this.initScrollWatch();
      this.initFooterWatch();
      this.updateVisibility();
    }

    connectedCallback() {
      this.initBuyWatch();
    }

    disconnectedCallback() {
      this.buyObserver?.disconnect();
      this.footerObserver?.disconnect();
      this.buyObserver = null;
      this.footerObserver = null;
      this.footerEl = null;
      clearTimeout(this.scrollIdleTimer);
      this.scrollIdleTimer = null;
      this.unbindScrollLikeEvents();
      if (this.onScrollLike) {
        window.removeEventListener("resize", this.onScrollLike);
        this.onScrollLike = null;
      }
    }
  }

  class SaProductServicesBenefits extends HTMLElement {
    connectedCallback() {
      this.tooltipInstances = [];
      this.initTooltipsWhenReady();
    }

    disconnectedCallback() {
      this.tooltipInstances.forEach((instance) => instance.destroy());
      this.tooltipInstances = [];
    }

    initTooltipsWhenReady() {
      const tryInit = () => this.initTooltips();

      if (this.querySelector("[data-sa-service-tooltip]")) {
        tryInit();
        return;
      }

      queueMicrotask(() => {
        if (this.querySelector("[data-sa-service-tooltip]")) {
          tryInit();
          return;
        }
        requestAnimationFrame(tryInit);
      });
    }

    initTooltips() {
      this.querySelectorAll("[data-sa-service-tooltip]").forEach((trigger) => {
        if (trigger._saServiceTooltip) return;
        const instance = createSaServiceTooltip(trigger);
        if (!instance) return;
        trigger._saServiceTooltip = instance;
        this.tooltipInstances.push(instance);
      });
    }
  }

  class SaProductShopPrescription extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector("[data-sa-lensadvisor-trigger]");
      this.card = this.querySelector(".sa-product-shop-prescription__card");
      this.productInfo = this.closest("sa-product-info");
      this.flowId = this.dataset.laFlowId || "";

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCardClick = this.onCardClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onPickerChange = this.onPickerChange.bind(this);
      this.onLensAdvisorReady = this.onLensAdvisorReady.bind(this);

      this.trigger?.addEventListener("click", this.onTriggerClick);
      this.card?.addEventListener("click", this.onCardClick);
      this.productInfo?.addEventListener(
        "sa:variant-change",
        this.onVariantChange,
      );
      this.productInfo?.addEventListener("change", this.onPickerChange);

      this.syncLensButton();
      this.syncSoldOutState();
      this.watchLensAdvisorReady();
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener("click", this.onTriggerClick);
      this.card?.removeEventListener("click", this.onCardClick);
      this.productInfo?.removeEventListener(
        "sa:variant-change",
        this.onVariantChange,
      );
      this.productInfo?.removeEventListener("change", this.onPickerChange);
      document.removeEventListener(
        "LensAdvizor:init:complete",
        this.onLensAdvisorReady,
      );
    }

    getLensButton() {
      return getLensButton();
    }

    syncLensButton() {
      const lensButton = getLensButton();
      if (!lensButton) return;

      const variantId = this.productInfo?.querySelector(
        'input[name="id"], [name="id"]',
      )?.value;
      if (variantId) {
        lensButton.setAttribute("data-la-variant-id", variantId);
      }

      if (this.flowId && !lensButton.getAttribute("data-la-flow-id")) {
        lensButton.setAttribute("data-la-flow-id", this.flowId);
      }
    }

    isLensAdvisorReady() {
      const lensButton = getLensButton();
      return (
        lensButton && typeof lensButton.launchLensAdvizorModal === "function"
      );
    }

    openLensAdvisor() {
      this.syncLensButton();
      return openLensAdvisor(this.productInfo);
    }

    watchLensAdvisorReady() {
      if (this.isLensAdvisorReady()) return;

      document.addEventListener(
        "LensAdvizor:init:complete",
        this.onLensAdvisorReady,
      );

      if (customElements.get("lensadvizor-btn")) {
        this.onLensAdvisorReady();
        return;
      }

      customElements.whenDefined("lensadvizor-btn").then(() => {
        this.onLensAdvisorReady();
      });
    }

    onLensAdvisorReady() {
      this.syncLensButton();
    }

    onTriggerClick(event) {
      event.preventDefault();
      if (this.classList.contains("is-sold-out")) return;
      if (usesLensApp(this.productInfo) && !isLensadvizorEnabled(this.productInfo)) {
        return;
      }

      if (this.openLensAdvisor()) return;

      const retry = () => {
        if (this.openLensAdvisor()) {
          document.removeEventListener("LensAdvizor:init:complete", retry);
        }
      };

      document.addEventListener("LensAdvizor:init:complete", retry);
      window.setTimeout(retry, 400);
      window.setTimeout(retry, 1200);
      window.setTimeout(retry, 2500);
    }

    onCardClick(event) {
      if (!this.classList.contains("is-sold-out")) return;
      event.preventDefault();
      event.stopPropagation();
    }

    getSelectedVariantInput() {
      return this.productInfo?.querySelector(
        "[data-sa-variant-picker] [data-sa-variant-input]:checked",
      );
    }

    getSoldOutName(variant, input) {
      const alias = input?.dataset.variantName?.trim();
      if (alias) return alias;

      const raw = variant?.option1 || variant?.title || "";
      return String(raw).split(" / ")[0].trim();
    }

    isVariantAvailable(input, variant) {
      if (typeof variant?.available === "boolean") return variant.available;
      if (input?.dataset.variantAvailable != null) {
        return input.dataset.variantAvailable !== "false";
      }
      return true;
    }

    isRxSoldOut(input, variant) {
      if (this.classList.contains("is-link")) return false;
      if (!usesLensApp(this.productInfo)) return false;
      if (!isLensadvizorFlag(input?.dataset.showLensadvizor)) return false;
      return !this.isVariantAvailable(input, variant);
    }

    syncSoldOutState(variant, input) {
      const selected = input || this.getSelectedVariantInput();
      const soldOut = this.isRxSoldOut(selected, variant);
      const name = this.getSoldOutName(variant, selected);

      this.classList.toggle("is-sold-out", soldOut);

      const subtitle = this.querySelector(
        ".sa-product-shop-prescription__subtitle",
      );
      if (subtitle) {
        const defaultSubtitle = subtitle.dataset.defaultSubtitle || "";
        if (soldOut) {
          const template =
            this.dataset.soldOutTemplate ||
            "Prescription lenses sold out for {name}.";
          subtitle.textContent = template.replaceAll("{name}", name);
          subtitle.hidden = false;
        } else {
          subtitle.innerHTML = defaultSubtitle;
          subtitle.hidden = defaultSubtitle === "";
        }
      }

      this.trigger =
        this.trigger || this.querySelector("[data-sa-lensadvisor-trigger]");
      if (this.trigger) {
        this.trigger.disabled = soldOut;
        if (soldOut) this.trigger.setAttribute("aria-disabled", "true");
        else this.trigger.removeAttribute("aria-disabled");
      }

      const card = this.card;
      if (card?.tagName === "A") {
        if (soldOut) {
          card.setAttribute("aria-disabled", "true");
          card.setAttribute("tabindex", "-1");
        } else {
          card.removeAttribute("aria-disabled");
          card.removeAttribute("tabindex");
        }
      }
    }

    onPickerChange(event) {
      const input = event.target?.closest?.("[data-sa-variant-input]");
      if (!input) return;
      this.syncSoldOutState(null, input);
    }

    onVariantChange(event) {
      const variant = event.detail?.variant;
      this.syncSoldOutState(variant);
      if (!variant) return;

      const lensButton = this.getLensButton();
      if (!lensButton) return;

      lensButton.setAttribute("data-la-variant-id", variant.id);
    }
  }

  function define(name, Class) {
    if (!customElements.get(name)) customElements.define(name, Class);
  }

  define("sa-product-info", SaProductInfo);
  define("sa-product-media", SaProductMedia);
  define("sa-quantity-input", SaQuantityInput);
  define("sa-product-countdown", SaProductCountdown);
  define("sa-product-coupon-copy", SaProductCouponCopy);
  define("sa-product-coupon-countdown", SaProductCouponCountdown);
  define("sa-product-key-features", SaProductKeyFeatures);
  define("sa-product-prescription", SaProductPrescription);
  define("sa-product-shop-prescription", SaProductShopPrescription);
  define("sa-product-review", SaProductReview);
  define("sa-product-bundle-recommendations", SaProductBundleRecommendations);
  define("sa-product-accessories", SaProductAccessories);
  define("sa-product-cart-present", SaProductAccessories);
  define("sa-product-sticky", SaProductSticky);
  define("sa-product-services-benefits", SaProductServicesBenefits);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () =>
      initDeliveryEstimates(),
    );
  } else {
    initDeliveryEstimates();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initDeliveryEstimates(event.target);
  });
})();
